/* ==========================================================================
   FOOTBALL-THEME :: "The Bend" - lock-the-line free kick prize game
   A popup mini-game that captures emails. Vanilla JS, no deps.
   See docs/freekick-game.md.

   How it works (game design)
   --------------------------
   - Waits N seconds, shows once per visitor, never for spun winners or
     logged-in customers (both optional). Two shots per player.
   - TAKE THE KICK -> the keeper prowls and the crowd hums. Tap anywhere in
     the goal (or aim the ghost sight + Space) and the shot curls exactly
     there. One tap = one shot.
   - The reticle locks, the shot curls in, the keeper dives. Goal or save.
   - The prize for each spot is drawn at the odds set in the theme editor
     (shown in the fine print: 0.004% = 1 in 25,000). Everybody who plays
     walks away with at least a fallback gift (free shipping) - it is never
     labelled as a promise, it lands as the surprise at the unlock.
   - Why odds and not raw aiming skill? A tiny corner target is either too
     easy to hit by timing (giving the jersey away constantly) or too fast
     to be playable. Drawing the prize from published odds and using the
     game as the reveal is the honest version of what every wheel/shootout
     game actually does - and it cannot be exploited (no scribbles, no
     timing tricks, no bots).
   - Emails post to Shopify's own customer form (/contact, form_type
     customer) and land as customers tagged freekick-game + the prize.
   ========================================================================== */
(function () {
  'use strict';

  var PREVIEW = !!(window.FS_FK_PREVIEW || window.FS_PEN_PREVIEW);
  var SVG_NS = 'http://www.w3.org/2000/svg';

  /* Geometry (SVG user units, viewBox 0 0 520 300) ------------------------- */
  var MOUTH = { x1: 68, x2: 452, y1: 72, y2: 210 };
  var BALL = { x: 260, y: 268 };

  /* Prize tiles - they cover the whole goal mouth. */
  var ZONES = {
    'top-left': { x1: 68, x2: 168, y1: 72, y2: 148, label: [118, 104, 'middle'] },
    'top-center': { x1: 168, x2: 352, y1: 72, y2: 148, label: [306, 114, 'middle'] },
    'top-right': { x1: 352, x2: 452, y1: 72, y2: 148, label: [402, 108, 'middle'] },
    'low-left': { x1: 68, x2: 168, y1: 148, y2: 210, label: [118, 182, 'middle'] },
    'low-center': { x1: 168, x2: 352, y1: 148, y2: 210, label: [306, 184, 'middle'] },
    'low-right': { x1: 352, x2: 452, y1: 148, y2: 210, label: [402, 182, 'middle'] }
  };
  var ZONE_READABLE = {
    'top-left': 'top-left postage stamp',
    'top-center': 'top centre',
    'top-right': 'top-right corner',
    'low-left': 'bottom-left corner',
    'low-center': 'bottom centre',
    'low-right': 'bottom-right corner'
  };

  /* Sweep tuning (viewBox units / second). The multiplier grows on every
     pass and carries from the width stage into the height stage. */
  var SWEEP = {
    x: { min: 78, max: 442, speed: 300 },
    y: { min: 82, max: 200, speed: 235 },
    xTick: [116, 372],
    yTick: [148],
    multStep: 1.12,
    multCap: 2.1
  };

  /* Keeper dive poses. translate(then) rotate, so the tipped body slides
     toward the corner as a whole. Arm/leg values are local. */
  var POSES = {
    'top-left': { rot: -72, tx: -36, ty: -22, armL: -168, armR: -146, legs: -16 },
    'top-center': { rot: 0, tx: 0, ty: -44, armL: -158, armR: 158, legs: 12 },
    'top-right': { rot: 72, tx: 36, ty: -22, armL: 146, armR: 168, legs: 16 },
    'low-left': { rot: -50, tx: -58, ty: 16, armL: -124, armR: -100, legs: -10 },
    'low-center': { rot: 0, tx: 0, ty: 16, armL: 16, armR: -16, legs: 0 },
    'low-right': { rot: 50, tx: 58, ty: 16, armL: 100, armR: 124, legs: 10 }
  };

  /* Fallback zones if the merchant section has no spot blocks. */
  var DEFAULT_ZONES = [
    { position: 'top-left', prize: 'FREE JERSEY WITH CLEATS', tag: 'MYTHIC', code: 'JERSEY1', chance: '0.001', locked: true },
    { position: 'top-right', prize: '20% OFF CLEATS', tag: 'LEGENDARY', code: 'CLEATS20', chance: '0.01', locked: true },
    { position: 'top-center', prize: 'GRIP SOCKS + BAG WITH CLEATS', tag: 'VERY RARE', code: 'SOCKS', chance: '0.1', locked: true },
    { position: 'low-left', prize: 'FREE SHIPPING', tag: 'RARE', code: 'KEEPER', chance: '8' },
    { position: 'low-center', prize: 'FREE SHIPPING', tag: 'COMMON', code: 'KEEPER', chance: '20' },
    { position: 'low-right', prize: 'FREE SHIPPING', tag: 'COMMON', code: 'KEEPER', chance: '20' }
  ];

  /* ---------------- small helpers ---------------- */
  function store(key, value) {
    if (PREVIEW) return value === undefined ? null : undefined;
    try {
      if (value === undefined) return window.localStorage.getItem(key);
      if (value === null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, String(value));
    } catch (e) {
      /* private mode - ignore */
    }
    return undefined;
  }

  function slug(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'prize';
  }

  function esc(text) {
    return String(text == null ? '' : text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function clamp(v, a, b) {
    return Math.min(b, Math.max(a, v));
  }

  function fmt(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function chanceText(chance) {
    if (!isFinite(chance) || chance <= 0) return 'unavailable';
    if (chance >= 100) return 'always';
    return '1 in ' + fmt(Math.round(100 / chance));
  }

  /* little prize icons (24x24, inherit colour) */
  var ICONS = {
    jersey: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M8.5 4 L4 6.4 5.9 10.4 8 9.4 V20 H16 V9.4 L18.1 10.4 20 6.4 15.5 4 C14.6 5.3 13.4 5.9 12 5.9 C10.6 5.9 9.4 5.3 8.5 4 Z"/></svg>',
    percent: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="7.4" cy="7.4" r="2.4"/><circle cx="16.6" cy="16.6" r="2.4"/><line x1="5.6" y1="18.4" x2="18.4" y2="5.6"/></svg>',
    truck: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2.8 7 H13.6 V15.6 H2.8 Z"/><path d="M13.6 10.2 H17.6 L20.6 13.2 V15.6 H13.6 Z"/><circle cx="6.4" cy="17.6" r="1.7"/><circle cx="17.2" cy="17.6" r="1.7"/></svg>',
    gift: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="9.6" width="16" height="10" rx="1.8"/><line x1="4" y1="13.6" x2="20" y2="13.6"/><line x1="12" y1="9.6" x2="12" y2="19.6"/><path d="M12 9.6 C9.4 9.6 8.2 8.4 8.6 6.9 C9 5.6 11 5.6 11.8 7.1 C12.4 8.2 12 9 12 9.6 Z"/><path d="M12 9.6 C14.6 9.6 15.8 8.4 15.4 6.9 C15 5.6 13 5.6 12.2 7.1 C11.6 8.2 12 9 12 9.6 Z"/></svg>',
    giftSealed: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3.4" y="9.8" width="17.2" height="10.8" rx="2"/><path d="M3.4 14 H20.6"/><path d="M12 9.8 V20.6"/><path d="M12 9.8 C9 9.8 7.6 8.4 8.1 6.7 C8.5 5.2 10.8 5.2 11.7 6.9 C12.4 8.2 12 9.2 12 9.8 Z"/><path d="M12 9.8 C15 9.8 16.4 8.4 15.9 6.7 C15.5 5.2 13.2 5.2 12.3 6.9 C11.6 8.2 12 9.2 12 9.8 Z"/><path d="M19.4 3.2 L20 4.7 L21.4 5.3 L20 5.9 L19.4 7.4 L18.8 5.9 L17.4 5.3 L18.8 4.7 Z" fill="currentColor" stroke="none"/></svg>',
    star: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3.6 L14.4 9.4 20.6 10 15.9 14.2 17.3 20.4 12 17.2 6.7 20.4 8.1 14.2 3.4 10 9.6 9.4 Z"/></svg>'
  };

  function iconFor(prize) {
    var p = String(prize || '').toUpperCase();
    if (p.indexOf('JERSEY') !== -1 || p.indexOf('SHIRT') !== -1 || p.indexOf('KIT') !== -1) return ICONS.jersey;
    if (p.indexOf('SOCK') !== -1) return ICONS.gift;
    if (p.indexOf('SHIPPING') !== -1 || p.indexOf('DELIVERY') !== -1) return ICONS.truck;
    if (p.indexOf('%') !== -1 || p.indexOf('OFF') !== -1) return ICONS.percent;
    return ICONS.gift;
  }

  /* ---- tier system: MYTHIC / LEGENDARY / VERY RARE / RARE / COMMON ---- */
  var TIERS = {
    mythic: { a: '#2a0a4a', b: '#7b2ff7', edge: '#c77dff', glow: 'rgba(160,80,255,0.55)', ink: '#f3e8ff', label: 'MYTHIC' },
    legendary: { a: '#4a2800', b: '#b8860b', edge: '#ffd76a', glow: 'rgba(255,200,80,0.5)', ink: '#fff3cf', label: 'LEGENDARY' },
    veryrare: { a: '#062a55', b: '#1e7fe0', edge: '#7fd0ff', glow: 'rgba(90,170,255,0.5)', ink: '#e8f6ff', label: 'VERY RARE' },
    rare: { a: '#08351a', b: '#1f9d4e', edge: '#7fe3a2', glow: 'rgba(80,220,140,0.45)', ink: '#eafff1', label: 'RARE' },
    common: { a: '#23272e', b: '#5d6672', edge: '#c0c8d4', glow: 'rgba(190,200,215,0.35)', ink: '#f4f7fb', label: 'COMMON' }
  };
  function tierFor(zone) {
    var tag = String(zone.tag || '').toUpperCase();
    if (tag.indexOf('MYTHIC') !== -1) return 'mythic';
    if (tag.indexOf('LEGEND') !== -1) return 'legendary';
    if (tag.indexOf('VERY RARE') !== -1) return 'veryrare';
    if (tag.indexOf('RARE') !== -1) return 'rare';
    if (tag.indexOf('COMMON') !== -1) return 'common';
    /* fallback: derive from a percentage prize */
    var m = /(\d+(\.\d+)?)\s*%/.exec(zone.prize || '');
    if (m) {
      var v = parseFloat(m[1]);
      if (v >= 15) return 'legendary';
      if (v >= 10) return 'veryrare';
      return 'rare';
    }
    return 'common';
  }

  /* the short label shown inside the goal tile - the tier name */
  function displayLabel(zone) {
    var t = TIERS[tierFor(zone)];
    return t ? t.label : 'PRIZE';
  }

  /* ornate tier plaque, in the style of the reference art */
  function tierBanner(tier, w) {
    var t = TIERS[tier] || TIERS.common;
    var uid = 'tb' + Math.random().toString(36).slice(2, 7);
    var s = '<svg class="fs-pen__tierbanner" viewBox="0 0 220 64" style="--tbw:' + (w || 132) + 'px" aria-hidden="true">';
    s += '<defs>';
    s += '<linearGradient id="' + uid + 'f" x1="0" y1="0" x2="0" y2="1">';
    s += '<stop offset="0" stop-color="' + shade(t.b, 1.4) + '"/><stop offset="0.4" stop-color="' + t.b + '"/><stop offset="1" stop-color="' + t.a + '"/>';
    s += '</linearGradient>';
    s += '<linearGradient id="' + uid + 'e" x1="0" y1="0" x2="0" y2="1">';
    s += '<stop offset="0" stop-color="#ffffff"/><stop offset="0.45" stop-color="' + t.edge + '"/><stop offset="1" stop-color="' + shade(t.a, 0.7) + '"/>';
    s += '</linearGradient>';
    s += '<linearGradient id="' + uid + 'g" x1="0" y1="0" x2="0" y2="1">';
    s += '<stop offset="0" stop-color="rgba(255,255,255,0.4)"/><stop offset="0.5" stop-color="rgba(255,255,255,0)"/><stop offset="1" stop-color="rgba(0,0,0,0.25)"/>';
    s += '</linearGradient>';
    s += '<linearGradient id="' + uid + 's" x1="0" y1="0" x2="1" y2="0">';
    s += '<stop offset="0" stop-color="rgba(255,255,255,0)"/><stop offset="0.5" stop-color="rgba(255,255,255,0.55)"/><stop offset="1" stop-color="rgba(255,255,255,0)"/>';
    s += '</linearGradient>';
    s += '<clipPath id="' + uid + 'c"><path d="M30 12 H190 L206 32 L190 52 H30 L14 32 Z"/></clipPath>';
    s += '</defs>';
    /* outer frame + dark outline */
    s += '<path d="M26 4 H194 L216 32 L194 60 H26 L4 32 Z" fill="url(#' + uid + 'e)" stroke="rgba(8,5,18,0.6)" stroke-width="1.6"/>';
    /* field + glass light + bevel */
    s += '<path d="M30 12 H190 L206 32 L190 52 H30 L14 32 Z" fill="url(#' + uid + 'f)"/>';
    s += '<path d="M30 12 H190 L206 32 H14 Z" fill="url(#' + uid + 'g)" opacity="0.85"/>';
    s += '<path d="M30 12 H190 L206 32 L190 52 H30 L14 32 Z" fill="none" stroke="rgba(255,255,255,0.35)" stroke-width="1.2"/>';
    /* moving sheen */
    s += '<g clip-path="url(#' + uid + 'c)"><rect class="fs-tb-sheen" x="-70" y="4" width="46" height="56" fill="url(#' + uid + 's)"/></g>';
    /* point gems */
    s += '<path d="M4 32 l10-6 v12 z" fill="' + t.ink + '"/><path d="M216 32 l-10-6 v12 z" fill="' + t.ink + '"/>';
    s += '<circle cx="9" cy="32" r="1.6" fill="rgba(10,6,20,0.5)"/><circle cx="211" cy="32" r="1.6" fill="rgba(10,6,20,0.5)"/>';
    /* top gem */
    s += '<path d="M110 0 l8 7 -8 7 -8-7 z" fill="' + t.ink + '" stroke="rgba(10,6,20,0.35)" stroke-width="1"/>';
    /* tier motifs */
    if (tier === 'mythic') {
      s += '<path d="M38 14 l-6 11 h5 l-9 16 3.5-12 h-5 z" fill="#efe2ff" opacity="0.95"/>';
      s += '<path d="M182 14 l6 11 h-5 l9 16 -3.5-12 h5 z" fill="#efe2ff" opacity="0.95"/>';
      s += '<path d="M50 22 q7 -9 15 -5 M170 22 q-7 -9 -15 -5" fill="none" stroke="rgba(240,225,255,0.8)" stroke-width="2" stroke-linecap="round"/>';
    } else if (tier === 'legendary') {
      s += '<path d="M40 18 q9 -9 18 1" fill="none" stroke="#fff0b8" stroke-width="2.6" stroke-linecap="round"/>';
      s += '<path d="M180 18 q-9 -9 -18 1" fill="none" stroke="#fff0b8" stroke-width="2.6" stroke-linecap="round"/>';
      s += '<path d="M110 10 l2.6 5.4 6 0.9 -4.3 4.2 1 6 -5.3-2.9 -5.3 2.9 1-6 -4.3-4.2 6-0.9 z" fill="#fff0b8" opacity="0.55"/>';
    } else if (tier === 'veryrare') {
      s += '<path d="M38 24 l6 -8 6 8 -6 8 z" fill="none" stroke="#cfeaff" stroke-width="2.2"/>';
      s += '<path d="M182 24 l-6 -8 -6 8 6 8 z" fill="none" stroke="#cfeaff" stroke-width="2.2"/>';
      s += '<path d="M52 18 v10 M47 23 h10" stroke="#cfeaff" stroke-width="1.6" opacity="0.8"/>';
    } else if (tier === 'rare') {
      s += '<path d="M36 28 q9 -11 18 0 q-9 11 -18 0 z" fill="#c9f7d9" opacity="0.9"/>';
      s += '<path d="M184 28 q-9 -11 -18 0 q9 11 18 0 z" fill="#c9f7d9" opacity="0.9"/>';
    } else {
      s += '<circle cx="40" cy="32" r="2.6" fill="' + t.ink + '" opacity="0.85"/>';
      s += '<circle cx="180" cy="32" r="2.6" fill="' + t.ink + '" opacity="0.85"/>';
      s += '<path d="M48 26 h8 M48 32 h8 M48 38 h8 M172 26 h-8 M172 32 h-8 M172 38 h-8" stroke="' + t.ink + '" stroke-width="1.4" opacity="0.5"/>';
    }
    /* label */
    s += '<text x="110" y="40" text-anchor="middle" font-size="22" font-weight="900" letter-spacing="1.6" fill="' + t.ink + '" stroke="rgba(8,4,18,0.65)" stroke-width="3.6" paint-order="stroke" style="font-family:inherit">' + t.label + '</text>';
    s += '</svg>';
    return s;
  }

  function tierGlowStyle(tier) {
    var t = TIERS[tier] || TIERS.common;
    return 'style="filter: drop-shadow(0 0 10px ' + t.glow + ')"';
  }
  var FLAME_OUT = 'M10 1 C12.2 6.2 17.5 8.6 17.5 14.2 C17.5 19.4 14.2 23.5 10 23.5 C5.8 23.5 2.5 19.4 2.5 14.2 C2.5 10.9 4.3 8.2 6.4 5.7 C6.9 7.6 7.8 9 9.2 10 C9.4 6.6 9.6 3.6 10 1 Z';
  var FLAME_IN = 'M10 8 C11.4 11 14 12.4 14 15.6 C14 18.7 12.3 21 10 21 C7.7 21 6 18.7 6 15.6 C6 13.8 7 12.4 8 11.2 C8.3 12.3 8.8 13.1 9.6 13.7 C9.7 11.7 9.8 9.7 10 8 Z';
  var SPARK = 'M0 -4.6 L1.1 -1.1 L4.6 0 L1.1 1.1 L0 4.6 L-1.1 1.1 L-4.6 0 L-1.1 -1.1 Z';
  var JERSEY_PATH = 'M8.5 4 L4 6.4 5.9 10.4 8 9.4 V20 H16 V9.4 L18.1 10.4 20 6.4 15.5 4 C14.6 5.3 13.4 5.9 12 5.9 C10.6 5.9 9.4 5.3 8.5 4 Z';

  function flameGroup(x, y, scale, variant, cls) {
    return '<g' + (cls ? ' class="' + cls + '"' : '') + ' transform="translate(' + x + ' ' + y + ') scale(' + scale + ')">' +
      '<path class="fs-emblem-flame ' + variant + '" d="' + FLAME_OUT + '" fill="#ff5e1e" opacity="0.9"/>' +
      '<path class="fs-emblem-flame ' + variant + '" d="' + FLAME_IN + '" fill="#ffd76a" opacity="0.95"/>' +
    '</g>';
  }

  function sparkAt(x, y, variant) {
    return '<g transform="translate(' + x + ' ' + y + ')">' +
      '<path class="fs-emblem-spark ' + variant + '" d="' + SPARK + '" fill="#ffd76a"/>' +
    '</g>';
  }

  function mythicEmblem() {
    return '<svg class="fs-emblem" viewBox="0 0 64 64" aria-hidden="true">' +
      flameGroup(4, 26, 0.8, 'fs-emblem-flame--b') +
      flameGroup(42, 26, 0.8, 'fs-emblem-flame--c') +
      flameGroup(20, 13, 1.15, 'fs-emblem-flame--a') +
      '<g transform="translate(17 16) scale(1.4)">' +
        '<path class="fs-emblem-jersey" d="' + JERSEY_PATH + '" fill="#ffd76a" stroke="#fff3c4" stroke-width="1.1" stroke-linejoin="round"/>' +
      '</g>' +
      sparkAt(10, 12, 'fs-emblem-spark--a') +
      sparkAt(54, 10, 'fs-emblem-spark--b') +
      sparkAt(52, 44, 'fs-emblem-spark--c') +
      sparkAt(12, 48, 'fs-emblem-spark--d') +
    '</svg>';
  }

  function reducedMotion() {
    if (window.fsPhysics && window.fsPhysics.reduced) return window.fsPhysics.reduced();
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function seeded(seed) {
    var st = (seed >>> 0) || 1;
    return function () {
      st = (st * 1664525 + 1013904223) >>> 0;
      return st / 4294967296;
    };
  }

  function shade(hex, f) {
    var m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    if (!m) return hex || '#9d00ff';
    var n = parseInt(m[1], 16);
    var r = Math.min(255, Math.round(((n >> 16) & 255) * f));
    var g = Math.min(255, Math.round(((n >> 8) & 255) * f));
    var b = Math.min(255, Math.round((n & 255) * f));
    var pad = function (v) {
      return ('0' + v.toString(16)).slice(-2);
    };
    return '#' + pad(r) + pad(g) + pad(b);
  }

  /* ---------------- sound (tiny WebAudio synth, no assets) ---------------- */
  function Sound() {
    this.on = false;
    this.ctx = null;
    this.noiseBuf = null;
  }
  Sound.prototype.ensure = function () {
    if (!this.ctx) {
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return false;
      this.ctx = new AC();
      var len = this.ctx.sampleRate * 1.8;
      this.noiseBuf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
      var data = this.noiseBuf.getChannelData(0);
      for (var i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    }
    if (this.ctx.state === 'suspended') this.ctx.resume();
    return true;
  };
  Sound.prototype.noise = function (dur, type, freq, q, peak, rampIn) {
    if (!this.on || !this.ensure()) return;
    var t = this.ctx.currentTime;
    var src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    src.loop = true;
    var f = this.ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    f.Q.value = q;
    var g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(peak, t + (rampIn || 0.01));
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f).connect(g).connect(this.ctx.destination);
    src.start(t);
    src.stop(t + dur + 0.05);
  };
  Sound.prototype.tone = function (f0, f1, dur, peak, type) {
    if (!this.on || !this.ensure()) return;
    var t = this.ctx.currentTime;
    var o = this.ctx.createOscillator();
    o.type = type || 'sine';
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(Math.max(30, f1), t + dur);
    var g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(peak, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(this.ctx.destination);
    o.start(t);
    o.stop(t + dur + 0.05);
  };
  Sound.prototype.kick = function () {
    this.tone(170, 48, 0.16, 0.5, 'sine');
    this.noise(0.05, 'lowpass', 320, 0.7, 0.35, 0.005);
  };
  Sound.prototype.glide = function (dur) {
    this.noise(Math.min(1.2, Math.max(0.3, dur)), 'bandpass', 620, 0.8, 0.06, 0.12);
  };
  Sound.prototype.clack = function () {
    this.tone(340, 190, 0.07, 0.3, 'triangle');
    this.noise(0.045, 'highpass', 2600, 0.8, 0.14, 0.003);
  };
  Sound.prototype.glove = function () {
    this.noise(0.1, 'bandpass', 1900, 1.1, 0.5, 0.004);
    this.tone(240, 120, 0.08, 0.22, 'triangle');
  };
  Sound.prototype.cheer = function () {
    this.noise(1.7, 'bandpass', 850, 0.55, 0.3, 0.3);
    this.noise(1.4, 'lowpass', 480, 0.6, 0.16, 0.25);
  };
  Sound.prototype.ding = function () {
    this.tone(880, 880, 0.45, 0.16, 'triangle');
    var self = this;
    window.setTimeout(function () {
      self.tone(1318, 1318, 0.5, 0.1, 'triangle');
    }, 90);
  };
  Sound.prototype.tick = function (lift) {
    this.tone(1050 + 520 * clamp(lift || 0, 0, 1), 780, 0.035, 0.09, 'triangle');
  };
  Sound.prototype.ohh = function () {
    this.noise(1.1, 'bandpass', 620, 0.9, 0.2, 0.08);
    this.tone(215, 120, 0.9, 0.05, 'sine');
  };
  Sound.prototype.swish = function () {
    this.noise(0.22, 'highpass', 3200, 0.7, 0.16, 0.01);
  };
  Sound.prototype.startAmbience = function () {
    if (!this.on || !this.ensure() || this.amb) return;
    var src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    src.loop = true;
    var f = this.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 420;
    f.Q.value = 0.4;
    var g = this.ctx.createGain();
    g.gain.value = 0.012;
    var lfo = this.ctx.createOscillator();
    lfo.frequency.value = 0.11;
    var lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 0.006;
    lfo.connect(lfoGain).connect(g.gain);
    src.connect(f).connect(g).connect(this.ctx.destination);
    src.start();
    lfo.start();
    this.amb = g;
  };
  Sound.prototype.ambience = function (level, dur) {
    if (!this.on || !this.ensure()) return;
    if (!this.amb) this.startAmbience();
    if (!this.amb) return;
    var t = this.ctx.currentTime;
    this.amb.gain.cancelScheduledValues(t);
    this.amb.gain.linearRampToValueAtTime(level, t + (dur || 0.4));
  };

  /* ---------------- one game instance ---------------- */
  function Game(root) {
    this.root = root;
    this.uid = root.getAttribute('data-uid') || 'x';
    this.config = null;
    this.zones = [];
    this.sound = new Sound();
    this.phase = 'idle'; /* idle -> aim-width -> aim-height -> locked -> flying -> resolved -> gate -> revealed */
    this.shotsLeft = 2;
    this.dom = null;
    this.outcome = null;
    this.sweep = null;
    this.sweepMult = 1;
    this.aimX = 260;
    this.aimY = 140;
    this.patrolX = 0;
    this.keeperOffset = 0;
    this.rafPatrol = 0;
  }

  Game.prototype.zoneByPos = function (pos) {
    if (this.layout && this.layout[pos]) return this.layout[pos];
    for (var i = 0; i < this.zones.length; i++) {
      if (this.zones[i].position === pos) return this.zones[i];
    }
    return null;
  };

  /* the prizes shuffle across the goal every play - the MYTHIC spot moves */
  Game.prototype.shuffleLayout = function () {
    var pool = this.zones.slice();
    for (var i = pool.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var swap = pool[i];
      pool[i] = pool[j];
      pool[j] = swap;
    }
    var keys = ['top-left', 'top-center', 'top-right', 'low-left', 'low-center', 'low-right'];
    this.layout = {};
    for (var k = 0; k < keys.length; k++) {
      this.layout[keys[k]] = pool[k % pool.length];
    }
  };

  Game.prototype.zoneAt = function (x, y) {
    var key = this.keyAt(x, y);
    return key ? this.zoneByPos(key) : null;
  };

  Game.prototype.keyAt = function (x, y) {
    for (var key in ZONES) {
      if (!ZONES.hasOwnProperty(key)) continue;
      var z = ZONES[key];
      if (x >= z.x1 && x <= z.x2 && y >= z.y1 && y <= z.y2) return key;
    }
    return null;
  };

  Game.prototype.init = function () {
    var self = this;
    var cfgEl = document.getElementById(this.root.getAttribute('data-config') || '');
    var config = {};
    if (cfgEl) {
      try {
        config = JSON.parse(cfgEl.textContent || '{}');
      } catch (e) {
        config = {};
      }
    }
    this.config = config;

    var zones = (config.zones || []).filter(function (z) {
      return z && ZONES[z.position];
    });
    this.zones = (zones.length ? zones : DEFAULT_ZONES).map(function (z) {
      var chance = parseFloat(z.chance);
      if (!isFinite(chance)) chance = 8;
      return {
        position: z.position,
        prize: String(z.prize || '').trim() || '7% OFF',
        tag: String(z.tag || '').trim(),
        code: String(z.code || '').trim(),
        chance: clamp(chance, 0, 100)
      };
    });

    var soundPref = store('fsPenalty.v1.sound');
    this.sound.on = soundPref === null || soundPref === undefined
      ? !!config.soundDefault && !PREVIEW
      : soundPref === '1';

    /* Theme editor: never auto-open, offer a test launcher instead. */
    if (window.Shopify && window.Shopify.designMode) {
      this.mountLauncher();
      return;
    }
    if (PREVIEW) {
      if (config.autoOpen !== false) {
        window.setTimeout(function () {
          self.open();
        }, Math.max(0, (config.delaySeconds || 30) * 1000));
      }
      return;
    }
    if (/[?&]fspen(=1)?\b/i.test(window.location.search)) {
      /* Test/demo link: always opens, clearing the played/dismissed state. */
      store('fsPenalty.v1.spun', null);
      store('fsPenalty.v1.dismissed', null);
      this.open();
      return;
    }

    if (this.root.getAttribute('data-customer') === 'true' && config.skipCustomers !== false) return;
    if (store('fsPenalty.v1.spun')) return;

    var dismissed = parseFloat(store('fsPenalty.v1.dismissed') || '0');
    var cooldownMs = (parseFloat(config.cooldownDays) || 7) * 86400000;
    if (dismissed && Date.now() - dismissed < cooldownMs) return;

    var delay = Math.max(0, (parseFloat(config.delaySeconds) || 30) * 1000);
    window.setTimeout(function () {
      if (store('fsPenalty.v1.spun')) return;
      if (!document.hidden) self.open();
      else {
        document.addEventListener('visibilitychange', function once() {
          document.removeEventListener('visibilitychange', once);
          if (store('fsPenalty.v1.spun')) return;
          self.open();
        });
      }
    }, delay);
  };

  Game.prototype.mountLauncher = function () {
    var self = this;
    var existing = document.querySelector('.fs-pen-launcher[data-uid="' + this.uid + '"]');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fs-pen-launcher';
    btn.setAttribute('data-uid', this.uid);
    btn.innerHTML = '<span aria-hidden="true">&#9917;</span> Test the free kick game';
    btn.addEventListener('click', function () {
      self.open();
    });
    document.body.appendChild(btn);
  };

  /* ---------------- DOM ---------------- */
  Game.prototype.build = function () {
    if (this.dom) return;
    var self = this;
    var cfg = this.config;
    this.shuffleLayout();

    var card = document.createElement('div');
    card.className = 'fs-pen__card';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-label', cfg.heading || 'BEAT THE GOALKEEPER');
    card.innerHTML =
      '<div class="fs-pen__bar">' +
        '<div class="fs-pen__tools">' +
          '<button type="button" class="fs-pen__icon-btn fs-pen__sound" aria-pressed="false" aria-label="Toggle sound">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="fs-pen__sound-on"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.5 5.5a9 9 0 0 1 0 13"/></svg>' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="fs-pen__sound-off"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none"/><line x1="22" y1="9" x2="16" y2="15"/><line x1="16" y1="9" x2="22" y2="15"/></svg>' +
          '</button>' +
          '<button type="button" class="fs-pen__icon-btn fs-pen__close" aria-label="Close">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/></svg>' +
          '</button>' +
        '</div>' +
      '</div>' +
      '<h2 class="fs-pen__title">' + esc(cfg.heading || 'BEAT THE GOALKEEPER') + '</h2>' +
      '<p class="fs-pen__sub">' + esc(cfg.subheading || 'Take your shot. Win a prize.') + '</p>' +
      '<div class="fs-pen__legend"></div>' +
      '<div class="fs-pen__stage"><div class="fs-pen__shaker"><div class="fs-pen__camera">' +
        this.svgMarkup() +
      '</div></div>' +
        '<div class="fs-pen__caption"><span></span></div>' +
      '</div>' +
      '<button type="button" class="fs-pen__action">' + esc(cfg.kickLabel || 'TAKE THE KICK') + '</button>' +
      '<div class="fs-pen__shots" aria-hidden="true"><i></i><i></i></div>' +
      '<div class="fs-pen__legal">' +
        '<div>' + esc(cfg.finePrint || '') + (cfg.termsUrl ? ' <a href="' + esc(cfg.termsUrl) + '">' + esc(cfg.termsLabel || 'Prizes & terms') + '</a>' : '') + '</div>' +
      '</div>' +
      '<p class="visually-hidden" aria-live="polite"></p>';

    this.root.innerHTML = '';
    this.root.appendChild(card);

    /* the reward/unlock overlay lives INSIDE the card, so the game window
       swaps its content instead of taking over the whole screen */
    var overlay = document.createElement('div');
    overlay.className = 'fs-pen__overlay';
    overlay.hidden = true;
    overlay.innerHTML =
      '<button type="button" class="fs-pen__icon-btn fs-pen__overlay-close" aria-label="Close">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/></svg>' +
      '</button>' +
      '<div class="fs-pen__panel fs-pen__gate">' +
        '<div class="fs-pen__gate-banner"></div>' +
        '<p class="fs-pen__gate-line"></p>' +
        '<p class="fs-pen__gate-ask"></p>' +
        '<form class="fs-pen__form" method="post" action="/contact" novalidate>' +
          '<input type="hidden" name="form_type" value="customer">' +
          '<input type="hidden" name="utf8" value="&#10003;">' +
          '<input type="hidden" name="contact[tags]" value="">' +
          '<input class="fs-pen__input" type="email" name="contact[email]" placeholder="' + esc(cfg.emailPlaceholder || 'Email address') + '" autocomplete="email" required>' +
          '<button class="fs-pen__submit" type="submit">' + esc(cfg.submitLabel || 'UNLOCK') + '</button>' +
          '<label class="fs-pen__consent"><input type="checkbox" name="contact[accepts_marketing]"' + (cfg.consentDefault ? ' checked' : '') + '> <span>' + esc(cfg.consentLabel || '') + '</span></label>' +
          '<p class="fs-pen__error" role="alert"></p>' +
        '</form>' +
      '</div>' +
      '<div class="fs-pen__panel fs-pen__reveal"></div>';
    card.appendChild(overlay);

    var overlayClose = overlay.querySelector('.fs-pen__overlay-close');
    if (overlayClose) {
      overlayClose.addEventListener('click', function () {
        self.close();
      });
    }

    this.dom = {
      card: card,
      stage: card.querySelector('.fs-pen__stage'),
      shaker: card.querySelector('.fs-pen__shaker'),
      cam: card.querySelector('.fs-pen__camera'),
      gate: this.root.querySelector('.fs-pen__gate'),
      form: this.root.querySelector('.fs-pen__form'),
      email: this.root.querySelector('.fs-pen__input'),
      submit: this.root.querySelector('.fs-pen__submit'),
      consent: this.root.querySelector('.fs-pen__consent input'),
      error: this.root.querySelector('.fs-pen__error'),
      reveal: this.root.querySelector('.fs-pen__reveal'),
      overlay: this.root.querySelector('.fs-pen__overlay'),
      badges: card.querySelector('.fs-pen__legend'),
      legend: card.querySelector('.fs-pen__legend'),
      shots: card.querySelector('.fs-pen__shots'),
      gateBanner: this.root.querySelector('.fs-pen__gate-banner'),
      gateLine: this.root.querySelector('.fs-pen__gate-line'),
      gateAsk: this.root.querySelector('.fs-pen__gate-ask'),
      caption: card.querySelector('.fs-pen__caption'),
      captionText: card.querySelector('.fs-pen__caption span'),
      action: card.querySelector('.fs-pen__action'),
      svg: card.querySelector('.fs-pen__svg'),
      keeper: card.querySelector('.fs-pen__keeper'),
      kBody: card.querySelector('.fs-pen__k-body'),
      kArmL: card.querySelector('.fs-pen__k-arm-l'),
      kArmR: card.querySelector('.fs-pen__k-arm-r'),
      kLegs: card.querySelector('.fs-pen__k-legs'),
      ball: card.querySelector('.fs-pen__ball'),
      ballShadow: card.querySelector('.fs-pen__ball-shadow'),
      trail: card.querySelector('.fs-pen__trail'),
      net: card.querySelector('.fs-pen__net'),
      aimV: card.querySelector('.fs-pen__aim--v'),
      aimH: card.querySelector('.fs-pen__aim--h'),
      reticle: card.querySelector('.fs-pen__reticle'),
      soundBtn: card.querySelector('.fs-pen__sound'),
      closeBtn: card.querySelector('.fs-pen__close'),
      live: card.querySelector('[aria-live]')
    };

    /* trail circles */
    this.trail = [];
    for (var i = 0; i < 12; i++) {
      var c = document.createElementNS(SVG_NS, 'circle');
      c.setAttribute('r', '0');
      c.setAttribute('fill', '#ffffff');
      c.setAttribute('opacity', '0');
      this.dom.trail.appendChild(c);
      this.trail.push(c);
    }

    this.renderLegend();
    this.fitZoneLabels();
    this.updateShotsUI();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        self.fitZoneLabels();
      });
    }

    this.dom.soundBtn.setAttribute('aria-pressed', this.sound.on ? 'true' : 'false');
    this.dom.soundBtn.addEventListener('click', function () {
      self.sound.on = !self.sound.on;
      self.dom.soundBtn.setAttribute('aria-pressed', self.sound.on ? 'true' : 'false');
      store('fsPenalty.v1.sound', self.sound.on ? '1' : '0');
      if (self.sound.on) self.sound.ding();
    });
    this.dom.closeBtn.addEventListener('click', function () {
      self.close();
    });
    this.root.addEventListener('click', function (e) {
      if (e.target === self.root) self.close();
    });
    document.addEventListener('keydown', function (e) {
      if (self.root.hidden) return;
      if (e.key === 'Escape') {
        self.close();
        return;
      }
      if (e.key === 'Tab') {
        self.trapTab(e);
        return;
      }
      /* SPACE on the pitch = shoot where the ghost sight sits. Buttons and
         inputs keep their native behaviour. */
      if ((e.code === 'Space' || e.key === ' ') && self.phase === 'aim') {
        if (e.repeat) return;
        var ae = document.activeElement;
        if (ae && (ae.tagName === 'BUTTON' || ae.tagName === 'INPUT' || ae.tagName === 'A' || ae.tagName === 'TEXTAREA')) return;
        e.preventDefault();
        self.shootAt(self.aimX, self.aimY);
      }
    });
    this.dom.form.addEventListener('submit', function (e) {
      /* Second pass: submitEmail() prepared the form and re-submits it so
         Shopify's captcha script (which only protects real /contact form
         posts) can attach its token. Let that one through untouched. */
      if (self._nativeSubmit) return;
      e.preventDefault();
      self.submitEmail();
    });
    this.dom.action.addEventListener('click', function () {
      if (self.phase === 'idle') self.startKick();
      else if (self.phase === 'aim') self.shootAt(self.aimX, self.aimY);
    });
    this.dom.svg.addEventListener('pointerdown', function (e) {
      if (e.button !== undefined && e.button !== 0) return;
      if (self.phase !== 'aim') return;
      e.preventDefault();
      var pt = self.svgPoint(e);
      self.shootAt(pt.x, pt.y);
    });
    this.dom.svg.addEventListener('pointermove', function (e) {
      if (self.phase !== 'aim') return;
      if (e.pointerType === 'touch') return;
      var pt = self.svgPoint(e);
      self.moveGhost(pt.x, pt.y);
    });
  };

  Game.prototype.svgMarkup = function () {
    var uid = this.uid;
    var acc = this.config.accent || '#9d00ff';
    var accMid = shade(acc, 1.25);
    var accDark = shade(acc, 0.5);
    var s = '<svg class="fs-pen__svg" viewBox="0 0 520 300" xmlns="' + SVG_NS + '">';
    s += '<defs>';
    s += '<linearGradient id="fsPenSky-' + uid + '" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0b101c"/><stop offset="1" stop-color="#151d30"/></linearGradient>';
    s += '<linearGradient id="fsPenGrass-' + uid + '" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#1a4a30"/><stop offset="1" stop-color="#0b2416"/></linearGradient>';
    s += '<linearGradient id="fsPenPost-' + uid + '" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fafcff"/><stop offset="1" stop-color="#c3ccda"/></linearGradient>';
    s += '<radialGradient id="fsPenBall-' + uid + '" cx="0.36" cy="0.3" r="0.95"><stop offset="0" stop-color="#ffffff"/><stop offset="0.72" stop-color="#eef2f7"/><stop offset="1" stop-color="#aeb8c6"/></radialGradient>';
    s += '<radialGradient id="fsPenGlow-' + uid + '" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stop-color="rgba(255,255,255,0.55)"/><stop offset="1" stop-color="rgba(255,255,255,0)"/></radialGradient>';
    s += '<radialGradient id="fsPenJersey-' + uid + '" cx="0.34" cy="0.2" r="1.05"><stop offset="0" stop-color="' + accMid + '"/><stop offset="0.55" stop-color="' + acc + '"/><stop offset="1" stop-color="' + accDark + '"/></radialGradient>';
    s += '<linearGradient id="fsPenBeam-' + uid + '" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="rgba(255,255,255,0.5)"/><stop offset="1" stop-color="rgba(255,255,255,0)"/></linearGradient>';
    s += '<radialGradient id="fsPenVig-' + uid + '" cx="0.5" cy="0.45" r="0.75"><stop offset="0.55" stop-color="rgba(0,0,0,0)"/><stop offset="1" stop-color="rgba(0,0,0,0.42)"/></radialGradient>';
    s += '<linearGradient id="fsPenBottom-' + uid + '" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="rgba(0,0,0,0)"/><stop offset="1" stop-color="rgba(0,0,0,0.35)"/></linearGradient>';
    s += '<radialGradient id="fsPenSkin-' + uid + '" cx="0.38" cy="0.3" r="1"><stop offset="0" stop-color="#f6c79b"/><stop offset="0.7" stop-color="#e8b48c"/><stop offset="1" stop-color="#cf9264"/></radialGradient>';
    s += '<linearGradient id="fsPenKitShade-' + uid + '" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="rgba(255,255,255,0.22)"/><stop offset="0.45" stop-color="rgba(255,255,255,0)"/><stop offset="1" stop-color="rgba(0,0,0,0.34)"/></linearGradient>';
    s += '<linearGradient id="fsPenShorts-' + uid + '" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#2b3648"/><stop offset="1" stop-color="#11182a"/></linearGradient>';
    s += '<linearGradient id="fsPenNetDepth-' + uid + '" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="rgba(2,6,12,0.55)"/><stop offset="1" stop-color="rgba(2,6,12,0.18)"/></linearGradient>';
    s += '<linearGradient id="fsPenBoard-' + uid + '" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="' + accDark + '"/><stop offset="0.5" stop-color="' + acc + '"/><stop offset="1" stop-color="' + accDark + '"/></linearGradient>';
    s += '<clipPath id="fsPenBallClip-' + uid + '"><circle cx="260" cy="' + BALL.y + '" r="15"/></clipPath>';
    s += '<clipPath id="fsPenClip-' + uid + '"><rect x="' + MOUTH.x1 + '" y="' + MOUTH.y1 + '" width="' + (MOUTH.x2 - MOUTH.x1) + '" height="' + (MOUTH.y2 - MOUTH.y1) + '"/></clipPath>';
    s += '</defs>';

    /* sky + stadium bowl: roof arc, tiered stands, crowd speckle */
    s += '<rect x="0" y="0" width="520" height="66" fill="url(#fsPenSky-' + uid + ')"/>';
    s += '<path d="M-10 22 Q260 -26 530 22 L530 0 L-10 0 Z" fill="rgba(255,255,255,0.05)"/>';
    s += '<path d="M-10 22 Q260 -26 530 22" fill="none" stroke="rgba(190,210,240,0.22)" stroke-width="1.6"/>';
    for (var tier = 0; tier < 4; tier++) {
      s += '<rect x="0" y="' + (16 + tier * 13) + '" width="520" height="9" fill="rgba(255,255,255,' + (0.028 + tier * 0.012).toFixed(3) + ')"/>';
    }
    s += '<rect x="0" y="60" width="520" height="6" fill="rgba(120,160,255,0.10)"/>';
    var rnd = seeded(11);
    for (var ci = 0; ci < 170; ci++) {
      var px = Math.round(rnd() * 520);
      var py = Math.round(10 + rnd() * 46);
      var pr = (1 + rnd() * 1.5).toFixed(1);
      var po = (0.05 + rnd() * 0.16).toFixed(2);
      var pc = ci % 23 === 0 ? '#ffd76a' : ci % 11 === 0 ? acc : '#dfe6f2';
      s += '<circle cx="' + px + '" cy="' + py + '" r="' + pr + '" fill="' + pc + '" opacity="' + po + '"/>';
    }
    /* floodlight rigs: housing, lamp grid, glow */
    s += '<ellipse cx="24" cy="8" rx="78" ry="28" fill="url(#fsPenGlow-' + uid + ')" opacity="0.5"/>';
    s += '<ellipse cx="496" cy="8" rx="78" ry="28" fill="url(#fsPenGlow-' + uid + ')" opacity="0.5"/>';
    s += '<rect x="22" y="11" width="4" height="55" fill="#20293b"/>';
    s += '<rect x="494" y="11" width="4" height="55" fill="#20293b"/>';
    for (var rig = 0; rig < 2; rig++) {
      var rx0 = rig === 0 ? 4 : 476;
      s += '<rect x="' + rx0 + '" y="0" width="40" height="13" rx="4" fill="#39445a" stroke="rgba(255,255,255,0.14)"/>';
      for (var lamp = 0; lamp < 4; lamp++) {
        s += '<circle cx="' + (rx0 + 8 + lamp * 9) + '" cy="6.5" r="2.6" fill="#fff7d6" opacity="0.95"/>';
      }
    }

    /* pitch: mowing bands + floodlight wash + markings */
    s += '<rect x="0" y="66" width="520" height="234" fill="url(#fsPenGrass-' + uid + ')"/>';
    var band = 0;
    for (var gy = 66; gy < 300; gy += 26, band++) {
      if (band % 2 === 0) s += '<rect x="0" y="' + gy + '" width="520" height="26" fill="rgba(255,255,255,0.028)"/>';
    }
    s += '<ellipse cx="260" cy="120" rx="330" ry="120" fill="rgba(255,255,255,0.045)"/>';
    s += '<polygon class="fs-pen__beam fs-pen__beam--l" points="10,2 235,250 -70,250" fill="url(#fsPenBeam-' + uid + ')" opacity="0.07" pointer-events="none"/>';
    s += '<polygon class="fs-pen__beam fs-pen__beam--r" points="510,2 590,250 285,250" fill="url(#fsPenBeam-' + uid + ')" opacity="0.07" pointer-events="none"/>';
    s += '<ellipse cx="150" cy="200" rx="150" ry="42" fill="rgba(255,255,255,0.035)" pointer-events="none"/>';
    s += '<ellipse cx="370" cy="200" rx="150" ry="42" fill="rgba(255,255,255,0.035)" pointer-events="none"/>';
    s += '<rect x="4" y="209" width="512" height="3" rx="1.5" fill="rgba(255,255,255,0.2)"/>';
    s += '<path d="M128 211 L128 238 L392 238 L392 211" fill="none" stroke="rgba(255,255,255,0.13)" stroke-width="3"/>';
    s += '<path d="M150 266 Q260 308 370 266" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="3"/>';
    s += '<ellipse cx="260" cy="266" rx="6.5" ry="3" fill="rgba(255,255,255,0.28)"/>';
    /* foreground LED board along the bottom edge */
    s += '<rect x="0" y="288" width="520" height="12" fill="url(#fsPenBoard-' + uid + ')" opacity="0.85"/>';
    s += '<rect x="0" y="288" width="520" height="2" fill="rgba(255,255,255,0.35)"/>';
    s += '<text x="130" y="297.5" text-anchor="middle" font-size="7.5" font-weight="900" letter-spacing="3" fill="rgba(255,255,255,0.85)" style="font-family:inherit">PREMIUMCLEATS</text>';
    s += '<text x="390" y="297.5" text-anchor="middle" font-size="7.5" font-weight="900" letter-spacing="3" fill="rgba(255,255,255,0.85)" style="font-family:inherit">BEAT THE KEEPER</text>';

    /* goal depth: stanchions + back net behind the mouth */
    s += '<path d="M68 72 L92 94 M452 72 L428 94 M68 210 L92 196 M452 210 L428 196" stroke="rgba(200,214,235,0.4)" stroke-width="3" stroke-linecap="round"/>';
    s += '<rect x="84" y="86" width="352" height="118" fill="none" stroke="rgba(200,214,235,0.16)" stroke-width="2"/>';

    /* net (sagging, clipped to the mouth) + inside shadow */
    s += '<rect x="58" y="62" width="414" height="158" fill="url(#fsPenNetDepth-' + uid + ')"/>';
    s += '<g class="fs-pen__net" clip-path="url(#fsPenClip-' + uid + ')" style="transform-origin:260px 140px">';
    for (var nx = MOUTH.x1 + 12; nx < MOUTH.x2; nx += 24) {
      s += '<line x1="' + nx + '" y1="' + MOUTH.y1 + '" x2="' + nx + '" y2="' + MOUTH.y2 + '" stroke="rgba(255,255,255,0.10)" stroke-width="1"/>';
    }
    for (var ny = MOUTH.y1 + 13; ny < MOUTH.y2; ny += 26) {
      s += '<path d="M' + MOUTH.x1 + ' ' + ny + ' Q260 ' + (ny + 7) + ' ' + MOUTH.x2 + ' ' + ny + '" fill="none" stroke="rgba(255,255,255,0.10)" stroke-width="1"/>';
    }
    s += '</g>';

    /* goal frame: ground shadow, posts, caps, accent band, highlights */
    s += '<ellipse cx="260" cy="216" rx="212" ry="7" fill="rgba(0,0,0,0.38)"/>';
    s += '<rect x="62" y="66" width="10" height="148" rx="5" fill="rgba(0,0,0,0.4)"/>';
    s += '<rect x="456" y="66" width="10" height="148" rx="5" fill="rgba(0,0,0,0.4)"/>';
    s += '<rect x="62" y="66" width="404" height="10" rx="5" fill="rgba(0,0,0,0.4)"/>';
    s += '<rect x="58" y="62" width="10" height="150" rx="5" fill="url(#fsPenPost-' + uid + ')" stroke="rgba(20,28,44,0.35)"/>';
    s += '<rect x="452" y="62" width="10" height="150" rx="5" fill="url(#fsPenPost-' + uid + ')" stroke="rgba(20,28,44,0.35)"/>';
    s += '<rect x="58" y="62" width="404" height="10" rx="5" fill="url(#fsPenPost-' + uid + ')" stroke="rgba(20,28,44,0.35)"/>';
    s += '<circle cx="63" cy="67" r="5.5" fill="#ffffff" stroke="rgba(20,28,44,0.3)"/>';
    s += '<circle cx="457" cy="67" r="5.5" fill="#ffffff" stroke="rgba(20,28,44,0.3)"/>';
    s += '<rect x="60" y="66" width="3.4" height="142" rx="1.7" fill="rgba(255,255,255,0.45)" opacity="0.6"/>';
    s += '<rect x="456.6" y="66" width="3.4" height="142" rx="1.7" fill="rgba(255,255,255,0.45)" opacity="0.6"/>';
    s += '<rect x="61" y="65" width="398" height="2.6" rx="1.3" fill="rgba(255,255,255,0.55)" opacity="0.6"/>';

    /* YOU VS <keeper> banner */
    var vs = 'YOU VS ' + String(this.config.keeperName || 'GOALKEEPER').toUpperCase();
    var vw = 44 + Math.round(vs.length * 7.6);
    s += '<g class="fs-pen__vs">' +
      '<rect x="' + (260 - vw / 2).toFixed(0) + '" y="24" width="' + vw + '" height="24" rx="12" fill="rgba(8,11,18,0.88)" stroke="rgba(255,255,255,0.22)" stroke-width="1.2"/>' +
      '<text x="260" y="40" text-anchor="middle" font-size="12" font-weight="800" letter-spacing="1.6" fill="#ffffff" opacity="0.95" style="font-family:inherit">' + esc(vs) + '</text>' +
    '</g>';

    /* keeper: illustrated kit, articulated for the dive (scaled to 90% so his
       height sits right under the crossbar) */
    s += '<g class="fs-pen__keeper" transform="translate(26 20.85) scale(0.9)">' +
      '<ellipse cx="260" cy="212" rx="38" ry="7" fill="rgba(0,0,0,0.38)"/>' +
      '<ellipse cx="260" cy="211" rx="22" ry="4" fill="rgba(0,0,0,0.3)"/>' +
      '<g class="fs-pen__k-body" style="transform-origin:260px 172px">' +
        '<g class="fs-pen__k-legs" style="transform-origin:260px 178px">' +
          '<rect x="244" y="180" width="13" height="21" rx="4.5" fill="' + accDark + '"/>' +
          '<rect x="263" y="180" width="13" height="21" rx="4.5" fill="' + accDark + '"/>' +
          '<rect x="244" y="184" width="13" height="3" fill="#ffffff" opacity="0.75"/>' +
          '<rect x="263" y="184" width="13" height="3" fill="#ffffff" opacity="0.75"/>' +
          '<path d="M238 201 L259 201 L259 208.5 L242 208.5 Q238 208.5 238 205 Z" fill="#f4f6fa"/>' +
          '<path d="M261 201 L282 201 Q282 208.5 278 208.5 L261 208.5 Z" fill="#f4f6fa"/>' +
          '<rect x="238" y="205.6" width="21" height="2.6" rx="1.2" fill="' + acc + '"/>' +
          '<rect x="261" y="205.6" width="21" height="2.6" rx="1.2" fill="' + acc + '"/>' +
        '</g>' +
        '<path d="M237 159 L283 159 L282 176 Q281 183 273 183 L247 183 Q239 183 238 176 Z" fill="url(#fsPenShorts-' + uid + ')" stroke="rgba(255,255,255,0.10)"/>' +
        '<rect x="239" y="160" width="3" height="14" rx="1.5" fill="' + accDark + '" opacity="0.8"/>' +
        '<rect x="278" y="160" width="3" height="14" rx="1.5" fill="' + accDark + '" opacity="0.8"/>' +
        '<path d="M234 124 Q234 108 248 107 L272 107 Q286 108 286 124 L284 154 Q283 161 275 161 L245 161 Q237 161 236 154 Z" fill="url(#fsPenJersey-' + uid + ')"/>' +
        '<path d="M234 124 Q234 108 248 107 L272 107 Q286 108 286 124 L284 154 Q283 161 275 161 L245 161 Q237 161 236 154 Z" fill="url(#fsPenKitShade-' + uid + ')"/>' +
        '<path d="M236 132 L284 132 L283.4 142 L236.6 142 Z" fill="rgba(255,255,255,0.20)"/>' +
        '<path d="M251 108 L260 116 L269 108 Q265 106.5 260 106.5 Q255 106.5 251 108 Z" fill="rgba(0,0,0,0.30)"/>' +
        '<circle cx="260" cy="122" r="3" fill="#ffffff" opacity="0.9"/>' +
        '<text x="260" y="152" text-anchor="middle" font-size="22" font-weight="900" fill="rgba(255,255,255,0.95)" stroke="rgba(0,0,0,0.25)" stroke-width="0.8" paint-order="stroke" style="font-family:inherit">1</text>' +
        '<path d="M236 124 Q236 110 248 108" fill="none" stroke="rgba(255,255,255,0.25)" stroke-width="2.4" stroke-linecap="round"/>' +
        '<g class="fs-pen__k-arm-l" style="transform-origin:243px 114px">' +
          '<rect x="230" y="112" width="15" height="44" rx="7.5" fill="url(#fsPenJersey-' + uid + ')"/>' +
          '<rect x="230" y="112" width="15" height="44" rx="7.5" fill="url(#fsPenKitShade-' + uid + ')"/>' +
          '<rect x="230.5" y="146" width="14" height="5" rx="2.5" fill="#ffffff" opacity="0.85"/>' +
          '<rect x="230.5" y="151" width="14" height="5" rx="2.5" fill="' + acc + '"/>' +
          '<rect x="228" y="155" width="18" height="23" rx="8.5" fill="#f4f6fa" stroke="rgba(20,28,44,0.25)"/>' +
          '<rect x="228" y="155" width="18" height="6.5" rx="3" fill="#cfd7e2"/>' +
          '<line x1="233" y1="172" x2="233" y2="176.5" stroke="rgba(20,28,40,0.22)" stroke-width="1.4"/>' +
          '<line x1="237" y1="172.5" x2="237" y2="177" stroke="rgba(20,28,40,0.22)" stroke-width="1.4"/>' +
          '<line x1="241" y1="172" x2="241" y2="176.5" stroke="rgba(20,28,40,0.22)" stroke-width="1.4"/>' +
        '</g>' +
        '<g class="fs-pen__k-arm-r" style="transform-origin:277px 114px">' +
          '<rect x="275" y="112" width="15" height="44" rx="7.5" fill="url(#fsPenJersey-' + uid + ')"/>' +
          '<rect x="275" y="112" width="15" height="44" rx="7.5" fill="url(#fsPenKitShade-' + uid + ')"/>' +
          '<rect x="275.5" y="146" width="14" height="5" rx="2.5" fill="#ffffff" opacity="0.85"/>' +
          '<rect x="275.5" y="151" width="14" height="5" rx="2.5" fill="' + acc + '"/>' +
          '<rect x="274" y="155" width="18" height="23" rx="8.5" fill="#f4f6fa" stroke="rgba(20,28,44,0.25)"/>' +
          '<rect x="274" y="155" width="18" height="6.5" rx="3" fill="#cfd7e2"/>' +
          '<line x1="279" y1="172" x2="279" y2="176.5" stroke="rgba(20,28,40,0.22)" stroke-width="1.4"/>' +
          '<line x1="283" y1="172.5" x2="283" y2="177" stroke="rgba(20,28,40,0.22)" stroke-width="1.4"/>' +
          '<line x1="287" y1="172" x2="287" y2="176.5" stroke="rgba(20,28,40,0.22)" stroke-width="1.4"/>' +
        '</g>' +
        '<rect x="254.5" y="98" width="11" height="10" rx="3.5" fill="#cf9264"/>' +
        '<circle cx="245.5" cy="91" r="3.2" fill="url(#fsPenSkin-' + uid + ')"/>' +
        '<circle cx="274.5" cy="91" r="3.2" fill="url(#fsPenSkin-' + uid + ')"/>' +
        '<ellipse cx="260" cy="90" rx="14" ry="15.5" fill="url(#fsPenSkin-' + uid + ')"/>' +
        '<ellipse cx="255" cy="85" rx="5" ry="7" fill="rgba(255,255,255,0.20)"/>' +
        '<path d="M245 92 C243.5 72 250 67 260 67 C270 67 276.5 72 275 92 C272.5 83 268 80.5 260 80.5 C252 80.5 247.5 83 245 92 Z" fill="#241a12"/>' +
        '<path d="M248 76 Q260 68 272 76" fill="none" stroke="rgba(255,255,255,0.14)" stroke-width="2" stroke-linecap="round"/>' +
        '<path d="M249.5 84.8 L256.2 86.4" stroke="#2b2119" stroke-width="1.7" stroke-linecap="round"/>' +
        '<path d="M270.5 84.8 L263.8 86.4" stroke="#2b2119" stroke-width="1.7" stroke-linecap="round"/>' +
        '<circle cx="253.8" cy="90.5" r="1.9" fill="#1d2330"/>' +
        '<circle cx="266.2" cy="90.5" r="1.9" fill="#1d2330"/>' +
        '<circle cx="254.4" cy="89.8" r="0.6" fill="#ffffff"/>' +
        '<circle cx="266.8" cy="89.8" r="0.6" fill="#ffffff"/>' +
        '<path d="M260 91 L259 95.5 L261.5 95.5" fill="none" stroke="rgba(120,70,35,0.5)" stroke-width="1.1" stroke-linecap="round"/>' +
        '<path d="M256 99.5 Q260 101.5 264 99.5" fill="none" stroke="#8a5636" stroke-width="1.5" stroke-linecap="round"/>' +
      '</g>' +
    '</g>';

    /* prize zones */
    for (var key in ZONES) {
      if (!ZONES.hasOwnProperty(key)) continue;
      s += this.zoneMarkup(key);
    }

    /* vignette + flash layer, then the aiming layer */
    s += '<rect x="0" y="0" width="520" height="300" fill="url(#fsPenVig-' + uid + ')" pointer-events="none"/>';
    s += '<rect x="0" y="240" width="520" height="60" fill="url(#fsPenBottom-' + uid + ')" pointer-events="none"/>';
    s += '<ellipse class="fs-pen__spot-glow" cx="260" cy="266" rx="46" ry="12" fill="#ffffff" opacity="0.10" pointer-events="none"/>';
    s += '<g class="fs-pen__crowd"></g>';
    s += '<g class="fs-pen__aim fs-pen__aim--v" style="transform:translateX(260px);display:none">' +
      '<rect class="fs-pen__aim-line" x="-2.2" y="56" width="4.4" height="160" rx="2.2" fill="' + acc + '" opacity="0.85"/>' +
      '<rect x="-0.7" y="59" width="1.4" height="154" rx="0.7" fill="#ffffff" opacity="0.8"/>' +
      '<path class="fs-pen__aim-handle" d="M0 48 l6.5 10.5 h-13 Z" fill="#ffffff"/>' +
      '<circle class="fs-pen__aim-handle" cx="0" cy="218" r="3.6" fill="#ffffff"/>' +
    '</g>';
    s += '<g class="fs-pen__aim fs-pen__aim--h" style="transform:translateY(140px);display:none">' +
      '<rect class="fs-pen__aim-line" x="64" y="-2.2" width="396" height="4.4" rx="2.2" fill="' + acc + '" opacity="0.85"/>' +
      '<rect x="66" y="-0.7" width="392" height="1.4" rx="0.7" fill="#ffffff" opacity="0.8"/>' +
      '<path class="fs-pen__aim-handle" d="M464 0 l10.5 6.5 v-13 Z" fill="#ffffff"/>' +
      '<circle class="fs-pen__aim-handle" cx="60" cy="0" r="3.6" fill="#ffffff"/>' +
    '</g>';
    s += '<g class="fs-pen__hint" pointer-events="none">' +
      '<g class="fs-pen__hint-ring">' +
        '<circle cx="260" cy="140" r="30" fill="none" stroke="#ffffff" stroke-width="2" opacity="0.85"/>' +
        '<circle cx="260" cy="140" r="30" fill="none" stroke="' + acc + '" stroke-width="5" opacity="0.35"/>' +
      '</g>' +
      '<rect x="178" y="222" width="164" height="28" rx="14" fill="rgba(6,8,14,0.82)" stroke="rgba(255,255,255,0.25)" stroke-width="1"/>' +
      '<text x="260" y="241" text-anchor="middle" font-size="13.5" font-weight="800" letter-spacing="1.6" fill="#ffffff" style="font-family:inherit">TAP TO SHOOT</text>' +
    '</g>';
    s += '<g class="fs-pen__reticle" style="transform:translate(260px, 140px);display:none">' +
      '<circle class="fs-pen__reticle-halo" r="26" fill="' + acc + '" opacity="0.18"/>' +
      '<circle class="fs-pen__reticle-spin" r="21" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1.6" stroke-dasharray="4 7"/>' +
      '<circle class="fs-pen__reticle-ring" r="15" fill="rgba(8,10,18,0.35)" stroke="' + acc + '" stroke-width="2.4"/>' +
      '<path d="M-23 0 h9 M23 0 h-9 M0 -23 v9 M0 23 v-9" stroke="#ffffff" stroke-width="2" stroke-linecap="round" fill="none"/>' +
      '<circle r="2.8" fill="#ffffff"/>' +
    '</g>';

    /* ball shadow stays on the pitch and follows the ball */
    s += '<ellipse class="fs-pen__ball-shadow" cx="260" cy="282" rx="16" ry="5" fill="rgba(0,0,0,0.4)"/>';
    s += '<g class="fs-pen__trail"></g>';
    s += '<g class="fs-pen__ball" style="transform-origin:260px ' + BALL.y + 'px">' +
      '<circle cx="260" cy="' + BALL.y + '" r="15" fill="url(#fsPenBall-' + uid + ')" stroke="rgba(20,28,44,0.4)" stroke-width="0.8"/>' +
      '<g clip-path="url(#fsPenBallClip-' + uid + ')">' +
        '<path d="M260 261.5 L260 252 M266.2 266 L274 263.5 M263.8 273.2 L267.5 281 M256.2 273.2 L252.5 281 M253.8 266 L246 263.5" stroke="rgba(20,28,44,0.5)" stroke-width="1.1" fill="none"/>' +
        '<circle cx="260" cy="252.5" r="4.2" fill="#232a38"/>' +
        '<circle cx="274.5" cy="263" r="4.2" fill="#232a38"/>' +
        '<circle cx="268" cy="281.5" r="4.2" fill="#232a38"/>' +
        '<circle cx="252" cy="281.5" r="4.2" fill="#232a38"/>' +
        '<circle cx="245.5" cy="263" r="4.2" fill="#232a38"/>' +
        '<polygon points="260,261.5 266.2,266 263.8,273.2 256.2,273.2 253.8,266" fill="#232a38" stroke="rgba(255,255,255,0.65)" stroke-width="0.9" stroke-linejoin="round"/>' +
        '<ellipse cx="260" cy="282" rx="13" ry="6" fill="rgba(20,28,44,0.16)"/>' +
      '</g>' +
      '<ellipse cx="253.5" cy="260.5" rx="6.2" ry="4.2" fill="rgba(255,255,255,0.55)" transform="rotate(-30 253.5 260.5)"/>' +
    '</g>';
    s += '</svg>';
    return s;
  };

  Game.prototype.zoneMarkup = function (key) {
    var meta = ZONES[key];
    var zone = this.zoneByPos(key) || { prize: '7% OFF', tag: '', chance: 8 };
    var tier = tierFor(zone);
    var mythic = tier === 'mythic';
    var lbl = meta.label;
    var anchor = lbl[2] === 'start' ? 'start' : 'middle';
    var lx = lbl[0];
    var ly = lbl[1];
    var prize = displayLabel(zone);
    var tw = Math.max(mythic ? 74 : 52, Math.round(prize.length * (mythic ? 8.6 : 7.3) + 14));
    var pillX = anchor === 'middle' ? lx - tw / 2 : lx - 9;
    var s = '<g class="fs-pen__zone fs-pen__zone--tier-' + tier + '" data-pos="' + key + '">';
    s += '<rect x="' + meta.x1 + '" y="' + meta.y1 + '" width="' + (meta.x2 - meta.x1) + '" height="' + (meta.y2 - meta.y1) + '"/>';
    /* open corner brackets: the centre of every spot stays clear */
    var ci = 7, cl = 15, cx1 = meta.x1 + ci, cy1 = meta.y1 + ci, cx2 = meta.x2 - ci, cy2 = meta.y2 - ci;
    s += '<path class="fs-pen__zone-corner" d="M' + cx1 + ' ' + (cy1 + cl) + ' V' + cy1 + ' H' + (cx1 + cl) +
      ' M' + (cx2 - cl) + ' ' + cy1 + ' H' + cx2 + ' V' + (cy1 + cl) +
      ' M' + cx2 + ' ' + (cy2 - cl) + ' V' + cy2 + ' H' + (cx2 - cl) +
      ' M' + (cx1 + cl) + ' ' + cy2 + ' H' + cx1 + ' V' + (cy2 - cl) + '"/>';
    s += '<rect class="fs-pen__zone-pill' + (mythic ? ' fs-pen__zone-pill--mythic' : '') + '" x="' + pillX + '" y="' + (ly - 13) + '" width="' + tw + '" height="17.5" rx="8.75"/>';
    s += '<text class="fs-pen__zone-label" x="' + lx + '" y="' + (ly + 1) + '" text-anchor="' + anchor + '">' + esc(prize) + '</text>';
    s += '<text class="fs-pen__zone-odds" x="' + lx + '" y="' + (ly + 15) + '" text-anchor="' + anchor + '">' + esc(chanceText(zone.chance)) + '</text>';
    s += '</g>';
    return s;
  };

  /* measure the real rendered text and fit every pill INSIDE its own tile,
     so nothing overflows and nothing crosses into a neighbouring box */
  Game.prototype.fitZoneLabels = function () {
    if (!this.dom || !this.dom.card) return;
    var zones = this.dom.card.querySelectorAll('.fs-pen__zone');
    var fit = function (zoneEl, textEl, pillEl, pad, minW) {
      if (!textEl || !pillEl) return null;
      var tile = zoneEl.querySelector('rect');
      var tileX = parseFloat(tile.getAttribute('x')) || 68;
      var tileW = parseFloat(tile.getAttribute('width')) || 100;
      var left = tileX + 3;
      var right = tileX + tileW - 3;
      var w = 60;
      try {
        w = textEl.getComputedTextLength();
      } catch (e) {
        /* keep the fallback */
      }
      var tw = Math.max(minW, Math.ceil(w + pad));
      /* shrink the font (max twice) if the text cannot fit the tile */
      for (var s = 0; s < 2 && tw > right - left; s++) {
        var cur = parseFloat(window.getComputedStyle(textEl).fontSize) || 12.5;
        textEl.style.fontSize = Math.max(8.5, cur - 2.5) + 'px';
        try {
          w = textEl.getComputedTextLength();
        } catch (e) {
          /* keep the fallback */
        }
        tw = Math.max(minW, Math.ceil(w + pad));
      }
      var anchor = textEl.getAttribute('text-anchor');
      var lx = parseFloat(textEl.getAttribute('x')) || 0;
      var px = anchor === 'middle' ? lx - tw / 2 : lx - pad / 2 - 2;
      var tx = lx;
      if (px < left) {
        px = left;
        tx = anchor === 'middle' ? px + tw / 2 : px + pad / 2 + 2;
      }
      if (px + tw > right) {
        px = right - tw;
        tx = anchor === 'middle' ? px + tw / 2 : px + pad / 2 + 2;
      }
      pillEl.setAttribute('x', px);
      pillEl.setAttribute('width', tw);
      textEl.setAttribute('x', tx);
      return { px: px, tw: tw, tx: tx };
    };
    for (var i = 0; i < zones.length; i++) {
      var z = zones[i];
      var main = fit(z, z.querySelector('.fs-pen__zone-label'), z.querySelector('.fs-pen__zone-pill'), 18, 40);
      var odds = z.querySelector('.fs-pen__zone-odds');
      if (odds && main) odds.setAttribute('x', main.tx);
      fit(z, z.querySelector('.fs-pen__zone-tag'), z.querySelector('.fs-pen__zone-tag-pill'), 14, 34);
    }
  };

  Game.prototype.renderLegend = function () {
    var wrap = this.dom.legend;
    if (!wrap) return;
    var order = ['mythic', 'legendary', 'veryrare', 'rare', 'common'];
    var groups = {};
    for (var i = 0; i < this.zones.length; i++) {
      var z = this.zones[i];
      var tier = tierFor(z);
      if (!groups[tier]) groups[tier] = [];
      var ct = chanceText(z.chance);
      if (groups[tier].indexOf(ct) === -1) groups[tier].push(ct);
    }
    var html = '';
    for (var t = 0; t < order.length; t++) {
      var key = order[t];
      var g = groups[key];
      if (!g || !g.length) continue;
      html += '<div class="fs-pen__tier-row fs-pen__tier-row--' + key + (key === 'mythic' ? ' is-mythic' : '') + '">' +
        '<span class="fs-pen__tier-plaque" ' + tierGlowStyle(key) + '>' + tierBanner(key, 126) + '</span>' +
        '<span class="fs-pen__tier-text"><i>' + esc(g.join(' / ')) + '</i></span>' +
      '</div>';
    }
    /* the save gift is deliberately not advertised here - it is the
       surprise at the unlock */
    wrap.innerHTML = html;
  };

  /* ---------------- aiming ---------------- */
  Game.prototype.setActionLabel = function (text) {
    this.dom.action.textContent = text;
  };

  /* Tap-to-shoot: one tap in the goal = the shot. The ghost sight follows
     the pointer on desktop; on touch you just tap. Space shoots where the
     sight currently sits (centre by default). */
  Game.prototype.startKick = function () {
    if (this.phase !== 'idle') return;
    this.phase = 'aim';
    this.sweepMult = 1;
    this.aimX = 260;
    this.aimY = 140;
    if (this.dom.aimV) this.dom.aimV.style.display = 'none';
    if (this.dom.aimH) this.dom.aimH.style.display = 'none';
    this.dom.action.classList.add('is-pulse');
    this.setActionLabel(this.config.stopLabel || 'TAP TO KICK');
    if (this.dom.stage) this.dom.stage.classList.add('is-aiming');
    this.showGhost();
    this.sound.ambience(0.02, 0.8);
    this.buzz(10);
    this.crowdFlash(3, 500);
    this.track('aim-start');
    this.announce('Tap the goal where you want to shoot.');
    this.dom.action.focus();
  };

  Game.prototype.svgPoint = function (evt) {
    var svg = this.dom.svg;
    var pt = { x: 260, y: 140 };
    try {
      if (svg.getScreenCTM && evt.clientX !== undefined) {
        var m = svg.getScreenCTM();
        if (m && m.inverse) {
          var p = new DOMPoint(evt.clientX, evt.clientY).matrixTransform(m.inverse());
          pt = { x: p.x, y: p.y };
        }
      } else if (evt.clientX !== undefined) {
        var r = svg.getBoundingClientRect();
        pt = {
          x: ((evt.clientX - r.left) / Math.max(1, r.width)) * 520,
          y: ((evt.clientY - r.top) / Math.max(1, r.height)) * 300
        };
      }
    } catch (e) {
      var r2 = svg.getBoundingClientRect();
      pt = {
        x: ((evt.clientX - r2.left) / Math.max(1, r2.width)) * 520,
        y: ((evt.clientY - r2.top) / Math.max(1, r2.height)) * 300
      };
    }
    return { x: clamp(pt.x, MOUTH.x1 + 6, MOUTH.x2 - 6), y: clamp(pt.y, MOUTH.y1 + 6, MOUTH.y2 - 6) };
  };

  Game.prototype.ghostTransform = function () {
    return 'translate(' + this.aimX.toFixed(1) + 'px, ' + this.aimY.toFixed(1) + 'px) scale(0.45)';
  };

  Game.prototype.showGhost = function () {
    var ret = this.dom.reticle;
    if (!ret) return;
    ret.style.transform = this.ghostTransform();
    ret.style.display = '';
    ret.style.opacity = '0.75';
    ret.classList.add('is-ghost');
    var hint = this.dom.svg && this.dom.svg.querySelector('.fs-pen__hint');
    if (hint) hint.style.opacity = '';
  };

  Game.prototype.moveGhost = function (x, y) {
    if (this.phase !== 'aim') return;
    this.aimX = x;
    this.aimY = y;
    var ret = this.dom.reticle;
    if (ret && ret.style.display !== 'none') {
      ret.style.transform = this.ghostTransform();
    }
    /* once the player is aiming, the hint gets out of the way */
    var hint = this.dom.svg && this.dom.svg.querySelector('.fs-pen__hint');
    if (hint) hint.style.opacity = '0';
  };

  Game.prototype.shootAt = function (x, y) {
    if (this.phase !== 'aim') return;
    var self = this;
    this.phase = 'locked';
    this.aimX = clamp(x, MOUTH.x1 + 8, MOUTH.x2 - 8);
    this.aimY = clamp(y, MOUTH.y1 + 8, MOUTH.y2 - 8);
    if (this.dom.stage) this.dom.stage.classList.remove('is-aiming');
    this.dom.action.classList.remove('is-pulse');
    this.dom.action.disabled = true;
    this.setActionLabel(this.config.kickLabelLocked || 'KICKING...');
    this.ripple(this.aimX, this.aimY);
    this.sound.clack();
    this.buzz(14);
    var ret = this.dom.reticle;
    if (ret) ret.classList.remove('is-ghost');
    this.showReticle();
    this.track('lock-aim');
    this._kickTimer = window.setTimeout(function () {
      self.kickBall();
    }, reducedMotion() ? 120 : 300);
  };

  /* the prize is drawn when the kick fires, from the odds of the spot the
     shot actually finishes on - the aim itself is 100% honest */
  Game.prototype.drawOutcome = function (x, y) {
    var key = this.keyAt(x, y);
    var zone = (key && this.zoneByPos(key)) || this.zoneByPos('low-center') || this.zones[0];
    /* shot 1 is always saved (secretly); shot 2 rolls the real odds */
    var firstShot = this.shotsLeft > 1;
    /* Spots whose prize is the free-shipping gift (the same gift a save
       pays out) always go in - identical payout, so the goal is free
       drama. The big tiers keep their real odds. */
    var sameGift = String(zone.code || '').toUpperCase() !== '' &&
      String(zone.code || '').toUpperCase() === String(this.config.consolationCode || '').toUpperCase();
    var scored = !firstShot && (sameGift || Math.random() * 100 < zone.chance);
    this.outcome = {
      scored: scored,
      zone: zone,
      key: key || (zone && zone.position) || 'low-center',
      x: clamp(x, MOUTH.x1 + 8, MOUTH.x2 - 8),
      y: clamp(y, MOUTH.y1 + 8, MOUTH.y2 - 8),
      tight: !scored && Math.random() < 0.3
    };
  };

  Game.prototype.startSweep = function (axis) {
    var self = this;
    var cfg = SWEEP[axis];
    this.sweep = { axis: axis, pos: cfg.min, dir: 1, last: performance.now() };
    var el = axis === 'x' ? this.dom.aimV : this.dom.aimH;
    el.style.display = '';
    var loop = function (now) {
      if (!self.sweep || self.sweep.axis !== axis) return;
      if (self.phase !== 'aim-width' && self.phase !== 'aim-height') return;
      var dt = Math.min(0.05, (now - self.sweep.last) / 1000);
      self.sweep.last = now;
      var speed = cfg.speed * (self.sweepMult || 1);
      var prevPos = self.sweep.pos;
      self.sweep.pos += self.sweep.dir * speed * dt;
      var ticks = axis === 'x' ? SWEEP.xTick : SWEEP.yTick;
      for (var bi = 0; bi < ticks.length; bi++) {
        if ((prevPos - ticks[bi]) * (self.sweep.pos - ticks[bi]) < 0) {
          self.sound.tick(Math.min(1, speed / 520));
        }
      }
      if (self.sweep.pos >= cfg.max) {
        self.sweep.pos = cfg.max;
        self.sweep.dir = -1;
        self.bumpMult();
      } else if (self.sweep.pos <= cfg.min) {
        self.sweep.pos = cfg.min;
        self.sweep.dir = 1;
        self.bumpMult();
      }
      el.style.transform = axis === 'x'
        ? 'translateX(' + self.sweep.pos.toFixed(1) + 'px)'
        : 'translateY(' + self.sweep.pos.toFixed(1) + 'px)';
      self._sweepRaf = window.requestAnimationFrame(loop);
    };
    this._sweepRaf = window.requestAnimationFrame(loop);
  };

  Game.prototype.bumpMult = function () {
    this.sweepMult = Math.min(SWEEP.multCap, (this.sweepMult || 1) * SWEEP.multStep);
  };

  Game.prototype.handleTap = function () {
    /* legacy two-line flow retired: a bare tap now shoots where the ghost
       sight sits (centre until the pointer moves). */
    if (this.phase === 'aim') this.shootAt(this.aimX, this.aimY);
    else if (this.phase === 'aim-width') this.lockAxis('x');
    else if (this.phase === 'aim-height') this.lockAxis('y');
  };

  Game.prototype.lockAxis = function (axis) {
    var self = this;
    if (!this.sweep) return;
    if (this._sweepRaf) {
      window.cancelAnimationFrame(this._sweepRaf);
      this._sweepRaf = 0;
    }
    var stop = this.sweep ? this.sweep.pos : (axis === 'x' ? 260 : 140);
    this.sweep = null;
    this.sound.clack();
    this.buzz(12);
    this.track(axis === 'x' ? 'lock-width' : 'lock-height');

    var el = axis === 'x' ? this.dom.aimV : this.dom.aimH;
    var tr = function (v) {
      return axis === 'x' ? 'translateX(' + v.toFixed(1) + 'px)' : 'translateY(' + v.toFixed(1) + 'px)';
    };

    var done = function () {
      el.style.transform = tr(stop);
      self.ripple(axis === 'x' ? stop : 260, axis === 'x' ? 140 : stop);
      if (axis === 'x') {
        self.aimX = stop;
        self.phase = 'aim-height';
        self.setActionLabel(self.config.stopLabel2 || 'STOP THE HEIGHT');
        self.announce('Width locked. Stop the height.');
        self.startSweep('y');
        self.dom.action.focus();
      } else {
        self.aimY = stop;
        self.phase = 'locked';
        self.dom.action.classList.remove('is-pulse');
        self.dom.action.disabled = true;
        self.setActionLabel(self.config.kickLabelLocked || 'KICKING...');
        self.showReticle();
        self._kickTimer = window.setTimeout(function () {
          self.kickBall();
        }, reducedMotion() ? 150 : 550);
      }
    };

    if (reducedMotion() || !el.animate) {
      done();
      return;
    }
    /* an impact wobble that settles exactly on the point you chose */
    var wob = Math.min(5, 1.6 + (this.sweepMult || 1) * 1.6);
    var anim = el.animate(
      [
        { transform: tr(stop), offset: 0 },
        { transform: tr(stop - wob), offset: 0.22, easing: 'cubic-bezier(0.4, 0.1, 0.6, 1)' },
        { transform: tr(stop + wob * 0.55), offset: 0.46, easing: 'cubic-bezier(0.4, 0.1, 0.6, 1)' },
        { transform: tr(stop - wob * 0.25), offset: 0.68, easing: 'cubic-bezier(0.4, 0.1, 0.6, 1)' },
        { transform: tr(stop + wob * 0.1), offset: 0.85, easing: 'cubic-bezier(0.4, 0.1, 0.6, 1)' },
        { transform: tr(stop), offset: 1 }
      ],
      { duration: 520, fill: 'forwards' }
    );
    anim.finished.then(function () {
      anim.cancel();
      done();
    });
  };

  Game.prototype.showReticle = function () {
    var ret = this.dom.reticle;
    /* Minimal lock-on: a tiny snap at the tapped point, then it vanishes
       before the ball flies - the shot itself is the show. */
    ret.style.transform = this.ghostTransform();
    ret.style.display = '';
    ret.style.opacity = '1';
    ret.classList.add('is-locked');
    if (!reducedMotion() && ret.animate) {
      var anim = ret.animate(
        [
          { transform: this.ghostTransform() + ' scale(1.9)', opacity: 0.9 },
          { transform: this.ghostTransform() + ' scale(1)', opacity: 1, offset: 0.55 },
          { transform: this.ghostTransform() + ' scale(0.9)', opacity: 0 }
        ],
        { duration: 300, easing: 'cubic-bezier(0.2, 0.9, 0.3, 1)', fill: 'forwards' }
      );
      var self = this;
      anim.finished.then(function () {
        try { anim.cancel(); } catch (e) {}
        if (self.phase === 'locked' || self.phase === 'flying') ret.style.display = 'none';
      });
    } else {
      ret.style.display = 'none';
    }
    var key = this.keyAt(this.aimX, this.aimY);
    var zone = key ? this.zoneByPos(key) : null;
    if (key && zone) {
      var zoneEl = this.dom.card.querySelector('.fs-pen__zone[data-pos="' + key + '"]');
      if (zoneEl) zoneEl.classList.add('is-aimed');
      this.announce('Aimed at ' + (zone.prize || 'the goal') + '. Here comes the shot.');
    }
  };

  /* ---------------- the kick ---------------- */
  Game.prototype.kickBall = function () {
    var self = this;
    if (this.phase !== 'locked') return;
    if (this.root.hidden) return;
    this.drawOutcome(this.aimX, this.aimY);
    var o = this.outcome;
    this.phase = 'flying';
    if (this.dom.reticle) this.dom.reticle.style.display = 'none';
    var aimedEls = this.dom.card.querySelectorAll('.fs-pen__zone.is-aimed');
    for (var ai = 0; ai < aimedEls.length; ai++) aimedEls[ai].classList.remove('is-aimed');
    if (this.dom.stage) this.dom.stage.classList.add('is-flying');

    this.clearAnims(this.dom.ball);
    /* freeze patrol, remember where the keeper is */
    window.cancelAnimationFrame(this.rafPatrol);
    this.rafPatrol = 0;
    var keeperX = 260 + this.patrolX;

    /* bezier through the air with a natural bend toward the corner */
    var p0 = { x: BALL.x, y: BALL.y };
    var p1 = { x: o.x, y: o.y };
    var bend = (260 - p1.x) * 0.35;
    var crl = { x: (p0.x + p1.x) / 2 + bend, y: (p0.y + p1.y) / 2 - 46 };
    var bez = function (t) {
      var u = 1 - t;
      return {
        x: u * u * p0.x + 2 * u * t * crl.x + t * t * p1.x,
        y: u * u * p0.y + 2 * u * t * crl.y + t * t * p1.y
      };
    };
    var len = 0;
    var prev = p0;
    for (var i = 1; i <= 24; i++) {
      var q = bez(i / 24);
      len += Math.hypot(q.x - prev.x, q.y - prev.y);
      prev = q;
    }
    var duration = clamp(len / 460, 0.7, 1.1);
    if (o.tight) duration *= 1.7;
    this.trailColor = o.scored && o.key === 'top-left' ? '#ffd76a' : '#ffffff';

    /* keeper dives: to the ball if he saves (hand aimed at it), the wrong way if he does not */
    var diveKey = o.scored ? this.wrongWayKey(o.key, keeperX) : o.key;
    var diveDur = reducedMotion() ? 150 : o.scored ? 480 : clamp(duration * 1000 * 0.85, 420, 1000);
    window.setTimeout(function () {
      self.pose(diveKey, diveDur, { x: o.x, y: o.y }, !o.scored);
    }, reducedMotion() ? 0 : 140);

    /* punch-in as the boot meets the ball */
    this.camera(1.1, o.x, o.y, 480);
    this.shakeSmall();
    this.sound.kick();
    this.sound.glide(duration);
    this.burst();
    this.buzz(30);
    this.crowdFlash(6, 500);
    if (o.tight) {
      window.setTimeout(function () {
        self.sound.ohh();
      }, duration * 1000 * 0.55);
    }
    this.track('kick', { zone: o.key, scored: o.scored });

    if (reducedMotion()) {
      this.dom.ball.style.transform =
        'translate(' + (p1.x - BALL.x) + 'px,' + (p1.y - BALL.y) + 'px) scale(0.76)';
      this.updateBallShadow(p1);
      this.renderTrail([]);
      this._arriveTimer = window.setTimeout(function () {
        self.arrive();
      }, 80);
      return;
    }

    var launch = function () {
      var start = performance.now();
      var spin = 860;
      var trail = [];
      var frame = function (now) {
        var p = clamp((now - start) / (duration * 1000), 0, 1);
        /* snappy launch, near constant after; tight saves hang late */
        var e;
        if (p < 0.07) {
          e = p * p / 0.07 * 0.4 + p * 0.6;
        } else if (o.tight) {
          var q = (p - 0.07) / 0.93;
          var qe = q < 0.55 ? q : 0.55 + (1 - Math.pow(1 - (q - 0.55) / 0.45, 1.7)) * 0.45;
          e = 0.07 + qe * 0.93;
        } else {
          e = p;
        }
        var pt = bez(e);
        var scale = 1 - 0.3 * e;
        var stretch = 1 + Math.sin(Math.min(1, e * 3) * Math.PI) * 0.06;
        self.dom.ball.style.transform =
          'translate(' + (pt.x - BALL.x) + 'px,' + (pt.y - BALL.y) + 'px) scale(' + (scale * stretch).toFixed(3) + ') rotate(' + (spin * e) + 'deg)';
        self.updateBallShadow(pt);
        trail.unshift({ x: pt.x, y: pt.y });
        if (trail.length > 15) trail.pop();
        self.renderTrail(trail);
        if (p < 1) self._flightRaf = window.requestAnimationFrame(frame);
        else self.arrive();
      };
      self._flightRaf = window.requestAnimationFrame(frame);
    };
    if (!reducedMotion() && this.dom.ball.animate) {
      /* 120ms run-up: the ball dips and swells, then snaps off the boot */
      try {
        var wind = this.dom.ball.animate(
          [
            { transform: 'translate(0px,0px) scale(1)' },
            { transform: 'translate(0px,9px) scale(1.07)', offset: 0.55, easing: 'cubic-bezier(0.3,0,0.7,1)' },
            { transform: 'translate(0px,0px) scale(0.94)', offset: 1, easing: 'cubic-bezier(0.2,0.9,0.3,1.2)' }
          ],
          { duration: 130, fill: 'forwards' }
        );
        wind.finished.then(function () { try { wind.cancel(); } catch (e) {} launch(); });
      } catch (e) { launch(); }
    } else {
      launch();
    }
  };

  /* the ball's ground shadow shrinks and eases back as the ball flies */
  Game.prototype.updateBallShadow = function (pt) {
    if (!this.dom.ballShadow) return;
    var k = clamp(1 - (BALL.y - pt.y) / 420, 0.35, 1);
    this.dom.ballShadow.setAttribute('cx', pt.x.toFixed(1));
    this.dom.ballShadow.setAttribute('cy', (268 - (BALL.y - pt.y) * 0.26).toFixed(1));
    this.dom.ballShadow.setAttribute('rx', (16 * k).toFixed(1));
    this.dom.ballShadow.setAttribute('opacity', (0.42 * k).toFixed(2));
  };

  /* a small divot of grass kicks up with the shot */
  Game.prototype.burst = function () {
    if (reducedMotion() || !this.dom.svg) return;
    for (var i = 0; i < 8; i++) {
      var c = document.createElementNS(SVG_NS, 'circle');
      var a = -Math.PI / 2 + (Math.random() - 0.5) * 1.9;
      var d = 24 + Math.random() * 36;
      c.setAttribute('cx', BALL.x);
      c.setAttribute('cy', BALL.y + 6);
      c.setAttribute('r', (1.2 + Math.random() * 1.8).toFixed(1));
      c.setAttribute('fill', i % 3 === 0 ? '#67b57f' : '#2f6b40');
      this.dom.svg.appendChild(c);
      var anim = c.animate(
        [
          { transform: 'translate(0,0)', opacity: 0.95 },
          { transform: 'translate(' + (Math.cos(a) * d).toFixed(1) + 'px,' + (Math.sin(a) * d * -1 + 14).toFixed(1) + 'px)', opacity: 0 }
        ],
        { duration: 380 + Math.random() * 260, easing: 'cubic-bezier(0.2, 0.7, 0.4, 1)' }
      );
      (function (el, an) {
        an.finished.then(function () {
          if (el.parentNode) el.parentNode.removeChild(el);
        });
      })(c, anim);
    }
  };

  /* tiny camera shake on impact */
  Game.prototype.shake = function () {
    var el = this.dom.shaker || this.dom.cam;
    if (reducedMotion() || !el || !el.animate) return;
    el.animate(
      [
        { transform: 'translate(0,0)' },
        { transform: 'translate(-4px,3px)' },
        { transform: 'translate(3px,-3px)' },
        { transform: 'translate(-2px,1px)' },
        { transform: 'translate(0,0)' }
      ],
      { duration: 300, easing: 'ease-out' }
    );
  };

  Game.prototype.shakeSmall = function () {
    var el = this.dom.shaker || this.dom.cam;
    if (reducedMotion() || !el || !el.animate) return;
    el.animate(
      [
        { transform: 'translate(0,0) scale(1)' },
        { transform: 'translate(0,2px) scale(1.012)' },
        { transform: 'translate(0,0) scale(1)' }
      ],
      { duration: 180, easing: 'cubic-bezier(0.3,0.7,0.3,1)' }
    );
  };

  /* WAAPI animations with fill:forwards override inline styles forever.
     Anything that gets reset (ball, keeper) must have them cancelled, or
     the next shot plays underneath a frozen animation  -  the vanishing
     ball on shot 2. */
  Game.prototype.clearAnims = function (el) {
    if (!el || !el.getAnimations) return;
    var list = el.getAnimations();
    for (var i = 0; i < list.length; i++) {
      try { list[i].cancel(); } catch (e) {}
    }
  };

  Game.prototype.renderTrail = function (trail) {
    for (var i = 0; i < this.trail.length; i++) {
      var c = this.trail[i];
      var t = trail[i];
      if (!t) {
        c.setAttribute('r', '0');
        c.setAttribute('opacity', '0');
        continue;
      }
      var k = 1 - i / this.trail.length;
      c.setAttribute('cx', t.x);
      c.setAttribute('cy', t.y);
      c.setAttribute('fill', this.trailColor || '#ffffff');
      c.setAttribute('r', (2 + 6 * k).toFixed(2));
      c.setAttribute('opacity', (0.4 * k).toFixed(2));
    }
  };

  Game.prototype.arrive = function () {
    var self = this;
    if (this.root.hidden) return;
    var o = this.outcome;
    if (this.dom.stage) this.dom.stage.classList.remove('is-flying');
    this.renderTrail([]);
    this.phase = 'resolved';
    this.shake();
    this.dom.reticle.style.opacity = '0.35';
    this.dom.aimV.style.opacity = '0.35';
    this.dom.aimH.style.opacity = '0.35';

    if (!o.scored) {
      this.sound.glove();
      this.flash(o.x, o.y);
      /* the rebound bounces off the glove: pushed along ball-shoulder */
      var nx = 0;
      var ny = 0;
      if (this._lastSaveShoulder) {
        var ddx = o.x - this._lastSaveShoulder.x;
        var ddy = o.y - this._lastSaveShoulder.y;
        var dl = Math.hypot(ddx, ddy) || 1;
        nx = ddx / dl;
        ny = ddy / dl;
      }
      var rebX = (o.x - BALL.x) * 0.6 + nx * 38;
      var rebY = (o.y - BALL.y) * 0.6 + 130 + ny * 10;
      /* punch it back out: glove smack, bounce, drop  -  then bake the end
         pose into the style and cancel, so nothing lingers over shot 2 */
      if (!reducedMotion() && this.dom.ball.animate) {
        var ballEl = this.dom.ball;
        var finalT = 'translate(' + rebX + 'px,' + rebY + 'px) scale(0.9) rotate(920deg)';
        var out = ballEl.animate(
          [
            { transform: ballEl.style.transform },
            { transform: 'translate(' + (rebX * 0.68).toFixed(1) + 'px,' + (rebY * 0.42 - 26).toFixed(1) + 'px) scale(0.96) rotate(560deg)', offset: 0.55, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)' },
            { transform: finalT }
          ],
          { duration: 640, easing: 'cubic-bezier(0.3, 0.2, 0.4, 1)', fill: 'forwards' }
        );
        out.finished.then(function () {
          ballEl.style.transform = finalT;
          try { out.cancel(); } catch (e) {}
        });
      }
      this.showCaption('SAVED!');
      this.camera(o.tight ? 1.18 : 1.1, o.x, o.y, o.tight ? 700 : 450);
      this.buzz(24);
      this.announce('Saved! Your prize is locked in.');
      /* the shadow follows the ball as it rebounds away */
      if (this.dom.ballShadow) {
        var tx = BALL.x + rebX;
        var ty = BALL.y + rebY;
        if (reducedMotion()) {
          this.dom.ballShadow.setAttribute('cx', tx.toFixed(1));
          this.dom.ballShadow.setAttribute('cy', ty.toFixed(1));
          this.dom.ballShadow.setAttribute('rx', '16');
          this.dom.ballShadow.setAttribute('opacity', '0.42');
        } else {
          var shadow = this.dom.ballShadow;
          var sxs = parseFloat(shadow.getAttribute('cx')) || BALL.x;
          var sys = parseFloat(shadow.getAttribute('cy')) || 268;
          var sa = shadow.animate(
            [
              { transform: 'translate(0px,0px)' },
              { transform: 'translate(' + (tx - sxs).toFixed(1) + 'px,' + (ty - sys).toFixed(1) + 'px)' }
            ],
            { duration: 480, easing: 'cubic-bezier(0.3, 0.2, 0.4, 1)', fill: 'forwards' }
          );
          (function (el, an, x2, y2) {
            an.finished.then(function () {
              el.setAttribute('cx', x2.toFixed(1));
              el.setAttribute('cy', y2.toFixed(1));
              el.setAttribute('rx', '16');
              el.setAttribute('opacity', '0.42');
              an.cancel();
            });
          })(shadow, sa, tx, ty);
        }
      }
    } else {
      this.sound.cheer();
      this.flash(o.x, o.y);
      this.goalFlash();
      this.crowdFlash(22, 1600);
      this.sound.swish();
      this.camera(1.16, o.x, o.y, 340);
      this.buzz([30, 40, 80]);
      /* the ball squashes into the net and settles */
      if (!reducedMotion() && this.dom.ball.animate) {
        var gBall = this.dom.ball;
        var gT = gBall.style.transform;
        var squash = gBall.animate(
          [
            { transform: gT },
            { transform: gT + ' scale(1.18, 0.8)', offset: 0.3, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)' },
            { transform: gT + ' scale(0.93, 1.06)', offset: 0.62 },
            { transform: gT }
          ],
          { duration: 480, easing: 'ease-out', fill: 'forwards' }
        );
        squash.finished.then(function () {
          gBall.style.transform = gT;
          try { squash.cancel(); } catch (e) {}
        });
      }
      if (this.dom.net) {
        /* the net bulges from the exact point the ball hit it */
        this.dom.net.style.transformOrigin = o.x + 'px ' + o.y + 'px';
        this.dom.net.classList.add('is-hit');
        window.setTimeout(function () {
          self.dom.net.classList.remove('is-hit');
        }, 600);
      }
      var topCorner = o.key === 'top-left';
      this.showCaption(topCorner ? 'TOP BINS!' : 'GOAL!', topCorner);
      this.announce('Goal! Your prize is locked in.');
    }

    if (this.shotsLeft > 1) {
      /* the first shot is always saved - set up the second shot */
      this.shotsLeft--;
      this.updateShotsUI();
      this._gateTimer = window.setTimeout(function () {
        self.resetPlay(self.config.kickLabel2 || 'TAKE SHOT 2');
      }, reducedMotion() ? 900 : 1900);
    } else {
      this.shotsLeft--;
      this.updateShotsUI();
      this._gateTimer = window.setTimeout(function () {
        self.showGate();
      }, reducedMotion() ? 200 : 900);
    }
    this._standTimer = window.setTimeout(function () {
      self.standUp();
    }, reducedMotion() ? 400 : 1400);
  };

  /* ---------------- keeper ---------------- */
  Game.prototype.startPatrol = function () {
    var self = this;
    if (this.rafPatrol) return;
    var frame = function (now) {
      if (self.phase !== 'idle' && self.phase !== 'aim' && self.phase !== 'aim-width' && self.phase !== 'aim-height') return;
      var offset;
      if (window.FS_FK_TEST_PATROL !== undefined) {
        offset = window.FS_FK_TEST_PATROL; /* preview/testing hook */
      } else {
        offset = 56 * Math.sin(now * 0.0019) + 12 * Math.sin(now * 0.0007 + 1.3);
      }
      /* while the player aims, the keeper reads them: he leans and
         crouches a little toward the side they're pointing at */
      var lean = 0, crouch = 0, tilt = 0;
      if (self.phase === 'aim') {
        lean = clamp((self.aimX - 260) * 0.07, -16, 16);
        crouch = 1.6 + 0.7 * Math.sin(now * 0.006);
        tilt = clamp((self.aimX - 260) * 0.012, -2.4, 2.4);
      }
      offset += lean;
      self.patrolX = offset;
      self.keeperOffset = offset;
      if (self.dom && self.dom.kBody) {
        var bob = (1.5 * Math.sin(now * 0.006)).toFixed(2);
        self.dom.kBody.style.transform = 'translate(' + offset.toFixed(2) + 'px,' + (parseFloat(bob) + crouch).toFixed(2) + 'px) rotate(' + tilt.toFixed(2) + 'deg)';
      }
      self.rafPatrol = window.requestAnimationFrame(frame);
    };
    this.rafPatrol = window.requestAnimationFrame(frame);
  };

  Game.prototype.wrongWayKey = function (endKey, keeperX) {
    /* He guesses a side: opposite the ball for wide shots, and picks a side
       for centre shots. Always a side dive (top or low), never the middle. */
    var end = ZONES[endKey] || ZONES['low-center'];
    var endCx = (end.x1 + end.x2) / 2;
    var goRight;
    if (endCx < 240) goRight = true;
    else if (endCx > 280) goRight = false;
    else goRight = keeperX < 260;
    var side = goRight ? ['top-right', 'low-right'] : ['top-left', 'low-left'];
    return side[Math.floor(Math.random() * side.length)];
  };

  /* Keeper dive. For saves the lead glove is aimed at the ball with inverse
     kinematics on the rigged arm, so the hand actually lands on it. */
  Game.prototype.pose = function (which, dur, target, isSave) {
    var p = POSES[which] || POSES['low-center'];
    var px = this.keeperOffset || 0;
    var rot = p.rot;
    var tx = p.tx;
    var ty = p.ty;
    var armL = p.armL;
    var armR = p.armR;
    var legs = p.legs;
    var leadSym = 0; /* 1 = right arm leads, -1 = left arm leads */
    this._lastSaveShoulder = null;

    if (isSave && target) {
      /* map the ball into the rig's inner coordinates */
      var innerX = (target.x - 26) / 0.9;
      var innerY = (target.y - 20.85) / 0.9;
      var phi = rot * Math.PI / 180;
      var cos = Math.cos(phi);
      var sin = Math.sin(phi);
      var rotv = function (vx, vy) {
        return { x: vx * cos - vy * sin, y: vx * sin + vy * cos };
      };
      var Hx = 260;
      var Hy = 172;
      var shL = rotv(243 - Hx, 114 - Hy);
      var shR = rotv(277 - Hx, 114 - Hy);
      var posL = { x: Hx + shL.x, y: Hy + shL.y };
      var posR = { x: Hx + shR.x, y: Hy + shR.y };
      var useRight = Math.abs(innerX - posR.x) < Math.abs(innerX - posL.x);
      var base = useRight ? posR : posL;
      var vX = innerX - base.x;
      var vY = innerY - base.y;
      var vLen = Math.hypot(vX, vY) || 1;
      var REACH = 52.8; /* shoulder -> glove centre, inner units */
      var shFinX = innerX - (vX / vLen) * REACH;
      var shFinY = innerY - (vY / vLen) * REACH;
      tx = clamp(shFinX - base.x - px, -130, 130);
      ty = clamp(shFinY - base.y, -100, 70);
      /* solve the lead arm's local angle so the glove sits on the ball */
      var g0x = useRight ? 6 : -6;
      var g0y = 52.5;
      var wX = innerX - (base.x + px + tx);
      var wY = innerY - (base.y + ty);
      var lead = (Math.atan2(g0x, g0y) - Math.atan2(wX, wY)) * 180 / Math.PI - rot;
      while (lead > 180) lead -= 360;
      while (lead < -180) lead += 360;
      lead = clamp(lead, -190, 190);
      var trail = clamp(lead * 0.55, -170, 170);
      if (useRight) {
        armR = lead;
        armL = trail;
        leadSym = 1;
      } else {
        armL = lead;
        armR = trail;
        leadSym = -1;
      }
      /* where the shoulder ended up, in game coordinates (for the rebound) */
      this._lastSaveShoulder = {
        x: (base.x + px + tx) * 0.9 + 26,
        y: (base.y + ty) * 0.9 + 20.85
      };
    }

    var fin = { rot: rot, tx: tx, ty: ty, armL: armL, armR: armR, legs: legs };
    this._lastDive = { px: px, p: fin };
    var opt = { duration: Math.max(1, dur), fill: 'forwards', easing: 'cubic-bezier(0.16, 0.84, 0.3, 1)' };

    if (reducedMotion() || !this.dom.kBody.animate) {
      this.dom.kBody.style.transform = 'translate(' + (px + tx) + 'px,' + ty + 'px) rotate(' + rot + 'deg)';
      this.dom.kArmL.style.transform = 'rotate(' + armL + 'deg)';
      this.dom.kArmR.style.transform = 'rotate(' + armR + 'deg)';
      this.dom.kLegs.style.transform = 'rotate(' + legs + 'deg)';
      return;
    }
    this._poseAnims = this._poseAnims || [];
    for (var i = 0; i < this._poseAnims.length; i++) this._poseAnims[i].cancel();

    var leadArmKeys = function (from, to) {
      if (!isSave) return [{ transform: 'rotate(' + from + 'deg)' }, { transform: 'rotate(' + to + 'deg)' }];
      /* a little catch snap right before contact */
      return [
        { transform: 'rotate(' + from + 'deg)' },
        { transform: 'rotate(' + (to * 1.05) + 'deg)', offset: 0.88 },
        { transform: 'rotate(' + to + 'deg)' }
      ];
    };

    /* dive physics: crouch-and-load, explosive arc that overshoots,
       then the body settles onto the final pose (glove IK stays exact).
       The trailing arm and legs lag a beat behind the lead arm. */
    var lagArmKeys = function (from, to) {
      return [
        { transform: 'rotate(' + from + 'deg)' },
        { transform: 'rotate(' + (from + (to - from) * 0.22).toFixed(1) + 'deg)', offset: 0.32 },
        { transform: 'rotate(' + to + 'deg)' }
      ];
    };
    var legsOpt = { duration: Math.max(1, dur * 1.08), fill: 'forwards', easing: 'cubic-bezier(0.16, 0.84, 0.3, 1)' };
    this._poseAnims = [
      this.dom.kBody.animate(
        [
          { transform: 'translate(' + px + 'px,0) rotate(0deg)' },
          { transform: 'translate(' + (px - tx * 0.05).toFixed(1) + 'px,5px) rotate(' + (rot * 0.07).toFixed(1) + 'deg)', offset: 0.16, easing: 'cubic-bezier(0.4, 0, 0.7, 1)' },
          { transform: 'translate(' + (px + tx * 1.05).toFixed(1) + 'px,' + (ty * 0.92 - 8).toFixed(1) + 'px) rotate(' + (rot * 1.05).toFixed(1) + 'deg)', offset: 0.68, easing: 'cubic-bezier(0.16, 0.84, 0.3, 1)' },
          { transform: 'translate(' + (px + tx) + 'px,' + ty + 'px) rotate(' + rot + 'deg)' }
        ],
        opt
      ),
      this.dom.kArmL.animate(leadSym === -1 ? leadArmKeys(-30, armL) : lagArmKeys(-30, armL), opt),
      this.dom.kArmR.animate(leadSym === 1 ? leadArmKeys(30, armR) : lagArmKeys(30, armR), opt),
      this.dom.kLegs.animate(
        [
          { transform: 'rotate(0deg)' },
          { transform: 'rotate(' + (legs * 0.3).toFixed(1) + 'deg)', offset: 0.35 },
          { transform: 'rotate(' + legs + 'deg)' }
        ],
        legsOpt
      )
    ];
  };

  Game.prototype.standUp = function () {
    if (!this._lastDive || !this.dom.kBody) return;
    var px = this._lastDive.px;
    var p = this._lastDive.p;
    if (reducedMotion() || !this.dom.kBody.animate) {
      this.dom.kBody.style.transform = 'translate(' + px + 'px,0)';
      this.dom.kArmL.style.transform = 'rotate(-30deg)';
      this.dom.kArmR.style.transform = 'rotate(30deg)';
      this.dom.kLegs.style.transform = 'rotate(0deg)';
      return;
    }
    var opt = { duration: 700, fill: 'forwards', easing: 'cubic-bezier(0.3, 0.85, 0.35, 1)' };
    var fromBody = 'translate(' + (px + p.tx) + 'px,' + p.ty + 'px) rotate(' + p.rot + 'deg)';
    /* bake the upright pose into inline styles when finished, then
       cancel  -  otherwise the fill freezes the keeper under patrol */
    var bake = function (el, anim, finalT) {
      anim.finished.then(function () {
        el.style.transform = finalT;
        try { anim.cancel(); } catch (e) {}
      });
    };
    bake(this.dom.kBody, this.dom.kBody.animate(
      [{ transform: fromBody }, { transform: 'translate(' + px + 'px,0) rotate(0deg)' }],
      opt
    ), 'translate(' + px + 'px,0) rotate(0deg)');
    bake(this.dom.kArmL, this.dom.kArmL.animate([{ transform: 'rotate(' + p.armL + 'deg)' }, { transform: 'rotate(-30deg)' }], opt), 'rotate(-30deg)');
    bake(this.dom.kArmR, this.dom.kArmR.animate([{ transform: 'rotate(' + p.armR + 'deg)' }, { transform: 'rotate(30deg)' }], opt), 'rotate(30deg)');
    bake(this.dom.kLegs, this.dom.kLegs.animate([{ transform: 'rotate(' + p.legs + 'deg)' }, { transform: 'rotate(0deg)' }], opt), 'rotate(0deg)');
    this._lastDive = null;
  };

  /* ---------------- scheduling / open / close ---------------- */
  Game.prototype.open = function () {
    if (!this.root.hidden) return;
    this.build();
    if (this.phase === 'aim' || this.phase === 'aim-width' || this.phase === 'aim-height' || this.phase === 'locked' || this.phase === 'flying') {
      this.shotsLeft = 2;
      this.resetPlay();
    }
    this.root.hidden = false;
    this.lastFocus = document.activeElement;
    document.documentElement.classList.add('fs-pen-lock');
    var self = this;
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        self.root.classList.add('is-open');
      });
    });
    this.startPatrol();
    this.dom.closeBtn.focus();
    this.track('shown');
  };

  Game.prototype.updateShotsUI = function () {
    if (!this.dom || !this.dom.shots) return;
    var dots = this.dom.shots.querySelectorAll('i');
    /* the circles fill in as each shot is taken: 0/2 -> 1/2 -> 2/2 */
    var taken = Math.max(0, dots.length - this.shotsLeft);
    for (var i = 0; i < dots.length; i++) {
      dots[i].className = i < taken ? 'is-on' : '';
    }
  };

  /* hard reset back to the start screen (used between shots and when a play is abandoned) */
  Game.prototype.resetPlay = function (btnLabel) {
    if (this._kickTimer) {
      window.clearTimeout(this._kickTimer);
      this._kickTimer = 0;
    }
    if (this._arriveTimer) {
      window.clearTimeout(this._arriveTimer);
      this._arriveTimer = 0;
    }
    if (this._gateTimer) {
      window.clearTimeout(this._gateTimer);
      this._gateTimer = 0;
    }
    if (this._standTimer) {
      window.clearTimeout(this._standTimer);
      this._standTimer = 0;
    }
    if (this._flightRaf) {
      window.cancelAnimationFrame(this._flightRaf);
      this._flightRaf = 0;
    }
    if (this._sweepRaf) {
      window.cancelAnimationFrame(this._sweepRaf);
      this._sweepRaf = 0;
    }
    this.sweep = null;
    this.phase = 'idle';
    this.aimX = 260;
    this.aimY = 140;
    this.trailColor = '#ffffff';
    if (!this.dom) return;
    this.dom.action.disabled = false;
    this.dom.action.style.display = '';
    this.setActionLabel(btnLabel || this.config.kickLabel || 'TAKE THE KICK');
    this.dom.aimV.style.display = 'none';
    this.dom.aimH.style.display = 'none';
    this.dom.aimV.style.opacity = '1';
    this.dom.aimH.style.opacity = '1';
    this.dom.reticle.style.display = 'none';
    this.dom.reticle.style.opacity = '0.35';
    if (this.dom.reticle.classList) this.dom.reticle.classList.remove('is-ghost', 'is-locked');
    if (this.dom.stage) this.dom.stage.classList.remove('is-aiming', 'is-flying');
    /* kill every lingering fill-forwards animation before resetting  - 
       this is what made the ball vanish on shot 2 */
    this.clearAnims(this.dom.ball);
    this.clearAnims(this.dom.ballShadow);
    this.clearAnims(this.dom.kBody);
    this.clearAnims(this.dom.kArmL);
    this.clearAnims(this.dom.kArmR);
    this.clearAnims(this.dom.kLegs);
    if (this.dom.ballShadow && this.dom.ballShadow.getAnimations) this.clearAnims(this.dom.ballShadow);
    this.dom.ball.style.transform = '';
    this.renderTrail([]);
    if (this.dom.ballShadow) {
      this.dom.ballShadow.setAttribute('cx', '260');
      this.dom.ballShadow.setAttribute('cy', '282');
      this.dom.ballShadow.setAttribute('rx', '16');
      this.dom.ballShadow.setAttribute('opacity', '0.4');
    }
    this.dom.overlay.hidden = true;
    this.dom.overlay.classList.remove('is-open');
    this.dom.gate.classList.remove('is-show');
    this.dom.reveal.classList.remove('is-show');
    this.dom.reveal.classList.remove('is-unlock');
    var aimed = this.dom.card.querySelectorAll('.fs-pen__zone.is-aimed');
    for (var i = 0; i < aimed.length; i++) aimed[i].classList.remove('is-aimed');
    if (this.dom.gate) this.dom.gate.classList.remove('is-win', 'is-save');
    this.camera(1, 260, 150, 300, 'cubic-bezier(0.3, 0.7, 0.3, 1)');
    this.standUp();
    /* patrol was cancelled when the kick fired  -  bring the keeper back
       to life for the next shot */
    this.rafPatrol = 0;
    this.startPatrol();
    this.updateShotsUI();
  };

  Game.prototype.close = function () {
    if (this.root.hidden) return;
    var self = this;
    var needReset = this.phase === 'aim' || this.phase === 'aim-width' || this.phase === 'aim-height' || this.phase === 'locked' || this.phase === 'flying';
    window.cancelAnimationFrame(this.rafPatrol);
    this.rafPatrol = 0;
    if (this._sweepRaf) {
      window.cancelAnimationFrame(this._sweepRaf);
      this._sweepRaf = 0;
    }
    if (this._flightRaf) {
      window.cancelAnimationFrame(this._flightRaf);
      this._flightRaf = 0;
    }
    if (this._kickTimer) {
      window.clearTimeout(this._kickTimer);
      this._kickTimer = 0;
    }
    if (this._arriveTimer) {
      window.clearTimeout(this._arriveTimer);
      this._arriveTimer = 0;
    }
    if (this._gateTimer) {
      window.clearTimeout(this._gateTimer);
      this._gateTimer = 0;
    }
    if (this._standTimer) {
      window.clearTimeout(this._standTimer);
      this._standTimer = 0;
    }
    if (this.sound.amb) this.sound.ambience(0, 0.3);
    if (this.phase === 'gate') {
      /* they played - never ask them again, even if they close now */
      this.markSpun();
    } else if (this.phase !== 'revealed' && this.phase !== 'resolved') {
      store('fsPenalty.v1.dismissed', Date.now());
      this.track('dismissed');
    }
    this.root.classList.remove('is-open');
    this.root.classList.add('is-closing');
    window.setTimeout(function () {
      self.root.hidden = true;
      self.root.classList.remove('is-closing');
      document.documentElement.classList.remove('fs-pen-lock');
      if (needReset) self.resetPlay();
      if (self.lastFocus && self.lastFocus.focus) self.lastFocus.focus();
    }, reducedMotion() ? 0 : 260);
  };

  Game.prototype.trapTab = function (e) {
    var scope = this.dom.overlay && !this.dom.overlay.hidden && this.dom.overlay.classList.contains('is-open')
      ? this.dom.overlay
      : this.dom.card;
    var nodes = scope.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])');
    var list = [];
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].disabled && nodes[i].offsetParent !== null) list.push(nodes[i]);
    }
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    var active = document.activeElement;
    var inList = false;
    for (var j = 0; j < list.length; j++) {
      if (list[j] === active) {
        inList = true;
        break;
      }
    }
    if (!inList) {
      e.preventDefault();
      first.focus();
      return;
    }
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  Game.prototype.track = function (stage, extra) {
    var detail = { stage: stage };
    for (var k in extra) if (extra.hasOwnProperty(k)) detail[k] = extra[k];
    try {
      document.dispatchEvent(new CustomEvent('fs:freekick', { detail: detail }));
      if (window.dataLayer) window.dataLayer.push(Object.assign({ event: 'freekick_game' }, detail));
    } catch (e) {
      /* ignore */
    }
  };

  Game.prototype.announce = function (text) {
    if (this.dom.live) this.dom.live.textContent = text;
  };

  Game.prototype.flash = function (x, y) {
    if (reducedMotion() || !this.dom.svg) return;
    var c = document.createElementNS(SVG_NS, 'circle');
    c.setAttribute('cx', x);
    c.setAttribute('cy', y);
    c.setAttribute('r', 26);
    c.setAttribute('fill', 'rgba(255,255,255,0.85)');
    c.setAttribute('class', 'fs-pen__flash');
    c.style.transformOrigin = x + 'px ' + y + 'px';
    this.dom.svg.appendChild(c);
    var a = c.animate(
      [{ opacity: 0.9, transform: 'scale(0.4)' }, { opacity: 0, transform: 'scale(2.4)' }],
      { duration: 480, easing: 'ease-out' }
    );
    a.finished.then(function () {
      if (c.parentNode) c.parentNode.removeChild(c);
    });
  };

  /* cinematic camera: zoom about a point in the scene */
  Game.prototype.camera = function (scale, x, y, dur, ease) {
    var cam = this.dom.cam;
    if (!cam) return;
    if (reducedMotion() || !cam.animate) {
      cam.style.transform = 'scale(' + scale + ')';
      this._camScale = scale;
      return;
    }
    cam.style.transformOrigin = ((x / 520) * 100).toFixed(2) + '% ' + ((y / 300) * 100).toFixed(2) + '%';
    var from = this._camScale || 1;
    this._camScale = scale;
    cam.animate(
      [{ transform: 'scale(' + from + ')' }, { transform: 'scale(' + scale + ')' }],
      { duration: dur, easing: ease || 'cubic-bezier(0.2, 0.7, 0.2, 1)', fill: 'forwards' }
    );
  };

  /* mobile haptics */
  Game.prototype.buzz = function (pattern) {
    try {
      if (navigator.vibrate) navigator.vibrate(pattern);
    } catch (e) {
      /* ignore */
    }
  };

  /* camera flashes twinkle in the crowd */
  Game.prototype.crowdFlash = function (count, spread) {
    if (reducedMotion() || !this.dom.svg) return;
    var g = this.dom.svg.querySelector('.fs-pen__crowd');
    if (!g) return;
    for (var i = 0; i < count; i++) {
      (function (self, idx) {
        window.setTimeout(function () {
          var c = document.createElementNS(SVG_NS, 'circle');
          c.setAttribute('cx', (14 + Math.random() * 492).toFixed(0));
          c.setAttribute('cy', (6 + Math.random() * 52).toFixed(0));
          c.setAttribute('r', '1.7');
          c.setAttribute('fill', '#ffffff');
          c.setAttribute('class', 'fs-pen__pop');
          g.appendChild(c);
          var a = c.animate(
            [{ opacity: 0 }, { opacity: 0.95, offset: 0.25 }, { opacity: 0 }],
            { duration: 420 + Math.random() * 300, easing: 'ease-out' }
          );
          (function (el) {
            a.finished.then(function () {
              if (el.parentNode) el.parentNode.removeChild(el);
            });
          })(c);
        }, idx * (spread / Math.max(1, count)));
      })(this, i);
    }
  };

  /* a white pulse over the whole stage */
  Game.prototype.goalFlash = function () {
    if (reducedMotion() || !this.dom.stage) return;
    var d = document.createElement('div');
    d.className = 'fs-pen__goal-flash';
    this.dom.stage.appendChild(d);
    var a = d.animate(
      [{ opacity: 0 }, { opacity: 0.5, offset: 0.2 }, { opacity: 0 }],
      { duration: 420, easing: 'ease-out' }
    );
    a.finished.then(function () {
      if (d.parentNode) d.parentNode.removeChild(d);
    });
  };

  /* settling ripple for the line locks */
  Game.prototype.ripple = function (x, y) {
    if (reducedMotion() || !this.dom.svg) return;
    var r = document.createElementNS(SVG_NS, 'circle');
    r.setAttribute('cx', x);
    r.setAttribute('cy', y);
    r.setAttribute('r', '8');
    r.setAttribute('fill', 'none');
    r.setAttribute('stroke', '#ffffff');
    r.setAttribute('stroke-width', '2');
    r.setAttribute('class', 'fs-pen__flash');
    r.style.transformOrigin = x + 'px ' + y + 'px';
    this.dom.svg.appendChild(r);
    var a = r.animate(
      [{ opacity: 0.4, transform: 'scale(0.5)' }, { opacity: 0, transform: 'scale(1.5)' }],
      { duration: 420, easing: 'ease-out' }
    );
    a.finished.then(function () {
      if (r.parentNode) r.parentNode.removeChild(r);
    });
  };

  Game.prototype.showCaption = function (text, gold) {
    var cap = this.dom.caption;
    /* letters land one by one, like a broadcast graphic */
    if (reducedMotion()) {
      this.dom.captionText.textContent = text;
    } else {
      var html = '';
      for (var i = 0; i < text.length; i++) {
        var ch = text.charAt(i);
        html += '<i style="animation-delay:' + (i * 45) + 'ms">' + (ch === ' ' ? '&nbsp;' : esc(ch)) + '</i>';
      }
      this.dom.captionText.innerHTML = html;
    }
    cap.classList.toggle('fs-pen__caption--gold', !!gold);
    cap.classList.add('is-show');
    window.setTimeout(function () {
      cap.classList.remove('is-show');
    }, reducedMotion() ? 1200 : 1500);
  };

  Game.prototype.showGate = function () {
    if (this.phase !== 'resolved') return;
    if (this.root.hidden) return;
    this.phase = 'gate';
    var self = this;
    this.camera(1.04, this.outcome.x, this.outcome.y, 1100, 'cubic-bezier(0.3, 0.7, 0.3, 1)');
    this.dom.action.style.display = 'none';
    var o = this.outcome;
    var saved = !o.scored;
    if (this.dom.gate) {
      this.dom.gate.classList.toggle('is-win', !saved);
      this.dom.gate.classList.toggle('is-save', saved);
    }
    /* a save shows no tier banner - the gift is the surprise at the unlock */
    if (this.dom.gateBanner) this.dom.gateBanner.innerHTML = saved ? '' : tierBanner(tierFor(o.zone), 184);
    if (this.dom.gateLine) {
      this.dom.gateLine.textContent = saved
        ? (this.config.gateLineLose || "The goalkeeper saved it... but he's feeling generous.")
        : (this.config.gateLineWin || 'GOAL!');
    }
    if (this.dom.gateAsk) {
      this.dom.gateAsk.textContent = saved
        ? (this.config.gateAskLose || "Enter your email to get the keeper's gift")
        : (this.config.gateAskWin || 'Enter your email to claim your prize');
    }
    this._activeSubmitLabel = this.config.submitLabel || 'UNLOCK';
    this.dom.submit.textContent = this._activeSubmitLabel;
    this.dom.overlay.hidden = false;
    window.requestAnimationFrame(function () {
      self.dom.overlay.classList.add('is-open');
      self.dom.gate.classList.add('is-show');
    });
    this.track('gate');
    window.setTimeout(function () {
      self.dom.email.focus();
    }, 420);
  };

  /* ---------------- email ---------------- */
  Game.prototype.submitEmail = function () {
    var self = this;
    var email = (this.dom.email.value || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.showError('That email looks off - mind checking it?');
      this.dom.email.setAttribute('aria-invalid', 'true');
      this.dom.email.focus();
      return;
    }
    this.dom.email.removeAttribute('aria-invalid');
    this.showError('');
    this.dom.submit.disabled = true;
    this.dom.submit.textContent = '...';
    this.playerEmail = email;

    var outcome = this.outcome || {};
    var zone = outcome.zone || null;
    var prize = (zone && outcome.scored)
      ? zone.prize
      : this.config.consolationLabel || "THE KEEPER'S GIFT";
    var tags = 'freekick-game, freekick-' + slug(prize);

    var fd = new FormData();
    fd.append('form_type', 'customer');
    fd.append('utf8', '\u2713');
    fd.append('contact[email]', email);
    fd.append('contact[tags]', tags);
    if (this.dom.consent && this.dom.consent.checked) fd.append('contact[accepts_marketing]', '1');

    var done = function (ok, message) {
      if (ok) {
        self.track('email', { prize: prize });
        if (zone && outcome.scored && zone.locked) {
          saveWaiting({ code: zone.code, prize: zone.prize, email: email, locked: true, ts: Date.now() });
        } else if (!outcome.scored) {
          saveWaiting({ code: self.config.consolationCode || 'KEEPER', prize: '', locked: false, ts: Date.now() });
        }
        self.reveal();
      } else {
        self.dom.submit.disabled = false;
        self.dom.submit.textContent = self._activeSubmitLabel || self.config.submitLabel || 'UNLOCK';
        self.showError(message || 'Something went wrong - try again.');
      }
    };

    if (PREVIEW) {
      window.setTimeout(function () {
        done(true);
      }, 450);
      return;
    }

    /* Submit through the real form, not fetch(): Shopify's captcha script
       only attaches its token to a native /contact form post (a bare fetch
       gets a 400 "Missing CAPTCHA token" page). The pending record lets the
       game pick up on the return page and show the prize reveal. */
    var tagsInput = this.dom.form.querySelector('input[name="contact[tags]"]');
    if (tagsInput) tagsInput.value = tags;
    if (zone && outcome.scored && zone.locked) {
      saveWaiting({ code: zone.code, prize: zone.prize, email: email, locked: true, ts: Date.now() });
    } else if (!outcome.scored) {
      saveWaiting({ code: this.config.consolationCode || 'KEEPER', prize: '', locked: false, ts: Date.now() });
    }
    try {
      store('fsPenalty.v1.pending', JSON.stringify({
        prize: prize,
        code: zone && outcome.scored ? zone.code : this.config.consolationCode,
        scored: !!(outcome.scored && zone),
        mythic: !!(zone && /mythic/i.test(zone.tag) && outcome.scored),
        tag: (zone && zone.tag) || '',
        locked: !!(zone && outcome.scored && zone.locked),
        email: email
      }));
    } catch (e) {
      /* ignore */
    }
    this._nativeSubmit = true;
    if (this.dom.form.requestSubmit) this.dom.form.requestSubmit();
    else this.dom.form.submit();
  };

  Game.prototype.showError = function (text) {
    if (!text) {
      this.dom.error.classList.remove('is-show');
      this.dom.error.textContent = '';
      return;
    }
    this.dom.error.textContent = text;
    this.dom.error.classList.add('is-show');
  };

  Game.prototype.reveal = function (pending) {
    var outcome = this.outcome || {};
    var zone = outcome.zone || null;
    var scored = pending ? !!pending.scored : !!outcome.scored && !!zone;
    var mythic = pending
      ? !!pending.mythic
      : !!(zone && /mythic/i.test(zone.tag) && scored);
    var locked = pending
      ? !!pending.locked
      : !!(zone && zone.locked && scored);
    var playerEmail = pending
      ? pending.email || ''
      : this.playerEmail || '';
    var prize = pending
      ? pending.prize
      : scored
        ? zone.prize
        : this.config.consolationLabel || "THE KEEPER'S GIFT";
    var code = pending
      ? pending.code
      : scored
        ? zone.code
        : this.config.consolationCode;
    code = String(code || '').trim();

    this.phase = 'revealed';
    this.markSpun();
    this.track('revealed', { prize: prize, code: code, scored: scored });

    this.dom.gate.classList.remove('is-show');
    this.dom.overlay.hidden = false;
    this.dom.overlay.classList.add('is-open');
    this.camera(1, 260, 150, 800, 'cubic-bezier(0.3, 0.7, 0.3, 1)');
    this.crowdFlash(6, 1200);
    this.buzz(scored ? [40, 60, 120] : 25);
    var reveal = this.dom.reveal;
    var sub = scored
      ? (this.config.goalSub || 'GOAL! Your prize is below.')
      : (this.config.savedSub || 'The goalkeeper saved it. Your gift is below.');

    /* the prize lands like it's already theirs: a ticket stub reserved
       for their email, one tap to shop with it auto-applied */
    var reservedNote = playerEmail
      ? 'Reserved for ' + playerEmail + '  -  tap below and it applies at checkout.'
      : 'Tap below and it applies at checkout.';
    reveal.innerHTML =
      (scored ? '<div class="fs-pen__burst' + (mythic ? ' is-mythic' : '') + '"></div>' : '') +
      (scored
        ? '<div class="fs-pen__reveal-banner" ' + tierGlowStyle(tierFor(zone)) + '>' + tierBanner(tierFor(zone), 196) + '</div>'
        : '') +
      '<div class="fs-pen__reveal-icon' + (mythic ? ' is-mythic' : '') + '">' + (mythic ? mythicEmblem() : iconFor(prize)) + '</div>' +
      '<div class="fs-pen__prize' + (mythic ? ' fs-pen__prize--mythic' : '') + '">' + esc(prize) + '</div>' +
      '<div class="fs-pen__unlock-ribbon"><span>UNLOCKED FOR YOU</span></div>' +
      '<p class="fs-pen__prize-sub">' + esc(reservedNote) + '</p>' +
      (code
        ? '<div class="fs-pen__ticket">' +
            '<span class="fs-pen__ticket-stub">YOUR CODE</span>' +
            '<span class="fs-pen__code">' + esc(code) + '</span>' +
            '<button type="button" class="fs-pen__copy">Copy</button>' +
          '</div>' +
          '<a class="fs-pen__apply" href="/discount/' + encodeURIComponent(code) + '?redirect=/collections/all">SHOP WITH IT APPLIED<span aria-hidden="true">  ></span></a>' +
          (this.config.codeNote && locked && !playerEmail
            ? '<p class="fs-pen__code-note">' + esc(this.config.codeNote) + '</p>'
            : '')
        : '<p class="fs-pen__prize-sub">' + esc(sub) + '</p>');
    reveal.classList.add('is-show', 'is-unlock');

    var copyBtn = reveal.querySelector('.fs-pen__copy');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var finish = function () {
          copyBtn.textContent = 'Copied!';
          window.setTimeout(function () {
            copyBtn.textContent = 'Copy';
          }, 1500);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(finish, finish);
        } else {
          finish();
        }
      });
    }

    this.sound.ding();
    if (scored) this.confetti(reveal);
    this.announce('Your prize: ' + prize + (code ? '. Code ' + code : ''));
    if (reveal.scrollIntoView) {
      reveal.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
  };

  Game.prototype.markSpun = function () {
    store('fsPenalty.v1.spun', '1');
    store('fsPenalty.v1.dismissed', null);
  };

  Game.prototype.confetti = function (host) {
    if (reducedMotion()) return;
    var accent = '#9d00ff';
    try {
      accent = window.getComputedStyle(this.root).getPropertyValue('--fs-pen-accent').trim() || accent;
    } catch (e) {
      /* keep the default */
    }
    var canvas = document.createElement('canvas');
    canvas.className = 'fs-pen__confetti';
    host.appendChild(canvas);
    var rect = host.getBoundingClientRect();
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    var ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    var colors = ['var', 'gold', 'white'];
    var pieces = [];
    for (var i = 0; i < 70; i++) {
      pieces.push({
        x: rect.width / 2 + (Math.random() - 0.5) * 120,
        y: rect.height * 0.35 + (Math.random() - 0.5) * 30,
        vx: (Math.random() - 0.5) * 7,
        vy: -3.4 - Math.random() * 5.4,
        s: 3 + Math.random() * 4.5,
        r: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.3,
        c: colors[i % colors.length]
      });
    }
    var frames = 0;
    var draw = function () {
      frames++;
      ctx.clearRect(0, 0, rect.width, rect.height);
      var alive = false;
      for (var j = 0; j < pieces.length; j++) {
        var p = pieces[j];
        p.vy += 0.26;
        p.x += p.vx;
        p.y += p.vy;
        p.r += p.vr;
        p.vx *= 0.995;
        if (p.y < rect.height + 20) alive = true;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.r);
        ctx.fillStyle = p.c === 'var' ? accent : p.c === 'gold' ? '#ffd76a' : 'rgba(255,255,255,0.92)';
        ctx.fillRect(-p.s / 2, -p.s / 2, p.s, p.s * 0.62);
        ctx.restore();
      }
      if (alive && frames < 240) window.requestAnimationFrame(draw);
      else if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    };
    window.requestAnimationFrame(draw);
  };

  /* ---------------- returning-winner reminder ---------------- */
  /* After a locked win, store the prize per browser. When the winner comes
     back later (a lot of time can pass before checkout), a small bar
     carries the code, the email hint and the "use it" link. It goes away
     when used or dismissed, and expires after 7 days. */
  var WAIT_KEY = 'fsPenalty.v1.waiting';
  var WAIT_DAYS = 7;

  function saveWaiting(info) {
    try {
      store(WAIT_KEY, JSON.stringify({
        code: info.code || '',
        prize: info.prize || '',
        email: info.email || '',
        locked: !!info.locked,
        ts: info.ts || Date.now()
      }));
    } catch (e) {
      /* ignore */
    }
  }

  function waitingBar() {
    var raw = store(WAIT_KEY);
    if (!raw) return;
    var w;
    try {
      w = JSON.parse(raw);
    } catch (e) {
      store(WAIT_KEY, null);
      return;
    }
    if (!w || !w.code || w.used || w.dismissed) return;
    if (w.ts && Date.now() - w.ts > WAIT_DAYS * 864e5) {
      store(WAIT_KEY, null);
      return;
    }
    if (document.querySelector('.fs-pen__waiting')) return;
    if (document.querySelector('.fs-pen__overlay.is-open')) return;

    var bar = document.createElement('div');
    bar.className = 'fs-pen__waiting';
    var isLocked = !!w.locked || !!w.email;
    var line = isLocked
      ? 'You won <strong>' + esc(w.prize || 'a prize') + '</strong> at Beat the Goalkeeper' +
        (w.email ? ' - use <strong>' + esc(w.email) + '</strong> at checkout' : ' - use the same email at checkout') + '.'
      : 'A gift from <strong>Beat the Goalkeeper</strong> is waiting - use code <strong>' + esc(w.code) + '</strong> at checkout.';
    bar.innerHTML =
      '<span class="fs-pen__waiting-text">' + line + '</span>' +
      '<a class="fs-pen__waiting-go" href="/discount/' + encodeURIComponent(w.code) + '?redirect=/collections/all">USE IT</a>' +
      '<button type="button" class="fs-pen__waiting-x" aria-label="Dismiss">\u00d7</button>';
    document.body.appendChild(bar);

    var go = bar.querySelector('.fs-pen__waiting-go');
    if (go) {
      go.addEventListener('click', function () {
        try {
          w.used = true;
          store(WAIT_KEY, JSON.stringify(w));
        } catch (e) {
          /* ignore */
        }
      });
    }
    var x = bar.querySelector('.fs-pen__waiting-x');
    if (x) {
      x.addEventListener('click', function () {
        try {
          w.dismissed = true;
          store(WAIT_KEY, JSON.stringify(w));
        } catch (e) {
          /* ignore */
        }
        if (bar.parentNode) bar.parentNode.removeChild(bar);
      });
    }
  }

  /* ---------------- boot ---------------- */
  function boot() {
    var roots = document.querySelectorAll('.fs-pen');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].dataset.fsReady === 'true') continue;
      roots[i].dataset.fsReady = 'true';
      var game = new Game(roots[i]);
      game.init();

      /* Resume a reveal after a full-form fallback submission. */
      if (!PREVIEW) {
        var pending = store('fsPenalty.v1.pending');
        if (pending) {
          try {
            var data = JSON.parse(pending);
            store('fsPenalty.v1.pending', null);
            game.open();
            game.outcome = {
              scored: !!data.scored,
              zone: data.scored ? { prize: data.prize, code: data.code, tag: data.tag || (data.mythic ? 'MYTHIC' : ''), locked: !!data.locked } : null,
              x: 260,
              y: 140,
              tight: false
            };
            game.reveal(data);
          } catch (e) {
            store('fsPenalty.v1.pending', null);
          }
        }
      }

      if (PREVIEW) {
        window.fsPenaltyGame = game;
      }
    }

    waitingBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
